/**
 * Pre-Checkout V2 JavaScript
 * Single-page layout with modal editing
 * Modular architecture: StateManager, ModalController, CalendarModule, GiftModule
 *
 * NOTE: This module uses ES6 (const, let, arrow functions) intentionally.
 * Pre-checkout targets modern browsers only (2020+). Legacy ES5 not required.
 * See audit JS-M2.
 */

(function() {
  'use strict';

  // [CRO QW12 · S3A-05] Localized calendar status labels (EN fallback). PRE_CHECKOUT_STRINGS
  // is emitted inline before this script loads (page.pre-checkout.liquid).
  const PCS = window.PRE_CHECKOUT_STRINGS || {};
  const CAL_TXT = {
    cutoff: (PCS.calendar && PCS.calendar.cutoff_passed) || 'Cut-off time passed',
    unavail: (PCS.calendar && PCS.calendar.product_unavailable) || 'Product unavailable',
    notAvail: (PCS.calendar && PCS.calendar.not_available) || 'NOT AVAILABLE'
  };

  // [CRO QW12 · S3A-05] Locale-aware full date formatters — the whole string (order +
  // separators + names) must come from Intl, not manual EN-order concatenation.
  const _PC_LANG = document.documentElement.lang || 'en';
  const _dfDateWeekday = new Intl.DateTimeFormat(_PC_LANG, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  // Inside a sentence: full words, no abbreviation dots ("Thursday, October 1" / "jeudi 1 octobre")
  const _dfDateSentence = new Intl.DateTimeFormat(_PC_LANG, { weekday: 'long', day: 'numeric', month: 'long' });

  // ==========================================================================
  // Main Module - PreCheckoutV2
  // ==========================================================================
  const PreCheckoutV2 = {
    // Configuration
    // Timezone: Mauritius is UTC+4 (Indian/Mauritius) with NO daylight saving time
    // This is geographically fixed and will not change
    config: {
      // Base rates (non-Prime, normal period)
      standardRate: 39900,        // Rs 399
      sameDayRate: 59900,         // Rs 599
      // Surge rates (non-Prime)
      surgeStandardRate: 59900,   // Rs 599
      surgeSameDayRate: 79900,    // Rs 799
      // Prime rates (normal, below threshold Rs 1,999)
      primeStandardBelowRate: 19900,  // Rs 199
      primeSameDayBelowRate: 29900,   // Rs 299
      // Prime rates (normal, above threshold)
      primeStandardAboveRate: 0,      // FREE
      primeSameDayAboveRate: 19900,   // Rs 199
      // Prime rates (surge, below threshold)
      primeSurgeBelowStdRate: 29900,  // Rs 299
      primeSurgeBelowSdRate: 49900,   // Rs 499
      // Prime rates (surge, above threshold)
      primeSurgeAboveStdRate: 19900,  // Rs 199
      primeSurgeAboveSdRate: 39900,   // Rs 399
      // Legacy Prime + Gifts rates
      legacyPrimeStandardRate: 0,     // FREE always
      legacyPrimeSameDayRate: 19900,  // Rs 199
      legacyPrimeSurgeStdRate: 19900, // Rs 199
      legacyPrimeSurgeSdRate: 39900,  // Rs 399
      // Threshold
      freeDeliveryThreshold: 199900,  // Rs 1,999
      // Legacy compat
      sameDayPrice: 59900,
      standardPrice: 39900,
      currencySymbol: 'Rs',
      tzOffsetHours: 4,        // UTC+4 (Mauritius - hardcoded)
      cutOffHour: 11,          // Same-day cutoff 11:30
      cutOffMinute: 30
    },

    // State
    state: {
      deliveryDate: null,
      deliveryType: 'standard',
      deliveryPrice: 0,
      lostDate: null, // a date an upsell extra pushed out, which the alert offers to keep
      giftMessage: {
        occasion: '',
        greeting: '',
        message: '',
        signature: ''
      },
      // The shopper edited or erased the gift message in this tab (set at that moment, by
      // GiftModule.touch). Until then it is the cart's own - Liquid put it in the fields -
      // and Continue does not write it.
      giftTouched: false,
      // A Continue from this tab has sent card keys. Whether that write went through is
      // not always known (lost answer, timeout), so from then on the cart may carry them.
      giftAttempted: false,
      // The shopper's draft of the order note: null = not touched in this tab (the cart's
      // note, which Liquid put in the field, stands), '' = emptied on purpose
      orderNotes: null
    },

    // Flags
    hasGiftProducts: false,
    isPrimeMember: false,
    initialized: false,
    checkFailed: false, // the date check threw (start-up or Continue): Continue fails open

    // ==========================================================================
    // Initialization
    // ==========================================================================
    init() {
      const appEl = document.querySelector('.pre-checkout-v2');
      if (!appEl) return;
      if (this.initialized) return;

      console.log('[PreCheckoutV2] Initializing...');
      this.initialized = true;

      // Read configuration from data attributes
      this.hasGiftProducts = appEl.dataset.hasGiftProducts === 'true';
      this.isPrimeMember = appEl.dataset.isPrimeMember === 'true';
      this.config.sameDayPrice = parseInt(appEl.dataset.sameDayPrice) || 57000;
      this.config.standardPrice = parseInt(appEl.dataset.standardPrice) || 37000;
      this.config.currencySymbol = appEl.dataset.currencySymbol || 'Rs';

      // Type labels from locale (passed via data attribute)
      try {
        this.config.typeLabels = JSON.parse(appEl.dataset.typeLabels || '{}');
      } catch (e) {
        this.config.typeLabels = {};
      }
      // Fallback labels if not provided
      this.config.typeLabels = Object.assign({
        same_day: 'Same-Day',
        same_day_surge: 'Same-Day Peak',
        standard: 'Scheduled',
        surge_pricing: 'Peak Season',
        today: 'Today',
        unavailable: 'Unavailable',
        error_select_date: 'Please select a delivery date to continue',
        fast_title: 'Fast',
        schedule_title: 'Standard',
        free: 'FREE'
      }, this.config.typeLabels);

      // Timezone hardcoded to Mauritius (UTC+4) - no external config needed.
      // Same-day cutoff is read from vertical_policy.same_day_cutoff_default (emitted as
      // sameDayConfig.cutoff) so this checkout and the PDP delivery module agree on when
      // same-day closes. Falls back to the config default (11:30) if absent/malformed.
      this.applyPolicyCutoff();

      // Show DodoPrime notice if applicable
      if (this.isPrimeMember) {
        const primeNotice = document.querySelector('[data-pcv2-prime-notice]');
        if (primeNotice) primeNotice.style.display = 'flex';
      }

      // Initialize modules (listeners only: none of these selects, prices or saves)
      ModalController.init();
      CalendarModule.init();
      DeliveryOptionsModule.init();  // V3 radio cards
      GiftModule.init();             // Gift modal
      NotesModule.init();
      CheckoutHandler.init();
      NeedsInputModule.init();
      ExtrasDateCheck.init();

      // Decide the delivery date NOW, synchronously, from what Liquid rendered (cart total,
      // stored attributes, availability payload) and what this tab remembers - before
      // anything is shown as selected or saved. No request stands between the first
      // paint, or Continue, and this pass.
      if (DeliveryOptionsModule._initialized) {
        try {
          StateManager.start();
        } catch (e) {
          // The date check failed (no or unreadable availability data, say): fail open.
          // Never show a date Continue would not post (NEXT-DAY ships preselected).
          // Continue still works (CheckoutHandler.failOpen), and the summary says to what.
          console.error('[PreCheckout] Start-up error:', e);
          this.checkFailed = true;
          try {
            StateManager.startUnchecked();
          } catch (uncheckedError) {
            console.error('[PreCheckout] Could not keep a date either:', uncheckedError);
            NoticeManager.show('error', {});
            try {
              DeliveryOptionsModule.clearSelection();
            } catch (clearError) {
              console.error('[PreCheckout] Could not clear the selection:', clearError);
            }
          }
          try {
            SidebarUpdater.update();
          } catch (summaryError) {
            console.error('[PreCheckout] Could not update the summary:', summaryError);
          }
        }
      }
      // What the cart change that reloaded this page ended in (message, focus, typed text)
      CartSync.afterReload();

      console.log('[PreCheckoutV2] Initialization complete');
    },

    /**
     * Override the hardcoded same-day cutoff with the cart vertical's policy value
     * (pcv2-availability-config -> sameDayConfig.cutoff, "HH:MM"). Single source of
     * truth shared with the PDP delivery module. No-op today (policy default == 11:30);
     * keeps the two surfaces in lockstep if a vertical's cutoff is ever changed.
     */
    applyPolicyCutoff() {
      if (!window.DDMAvailability || typeof window.DDMAvailability.parseJSONScript !== 'function') return;
      var payload = window.DDMAvailability.parseJSONScript('pcv2-availability-config');
      var raw = payload && payload.sameDayConfig && payload.sameDayConfig.cutoff;
      var m = /^(\d{1,2}):(\d{2})$/.exec(raw || '');
      if (!m) return;
      var h = +m[1], min = +m[2];
      if (h > 23 || min > 59) return;
      this.config.cutOffHour = h;
      this.config.cutOffMinute = min;
    }
  };

  // ==========================================================================
  // DDMAvailability Dependency - Wait for availability module to load
  // ==========================================================================
  /**
   * Wait for DDMAvailability module to be ready
   * Resolves immediately if already loaded, otherwise listens for ready event
   * @param {number} timeoutMs - Max wait time (default 3000ms)
   * @returns {Promise<boolean>} - true if loaded, false if timed out
   */
  function waitForDDMAvailability(timeoutMs) {
    timeoutMs = timeoutMs || 3000;

    return new Promise(function(resolve) {
      // Already loaded
      if (window.DDMAvailability) {
        resolve(true);
        return;
      }

      var resolved = false;

      // Listen for ready event
      function onReady() {
        if (resolved) return;
        resolved = true;
        document.removeEventListener('ddm:availability:ready', onReady);
        resolve(true);
      }

      document.addEventListener('ddm:availability:ready', onReady);

      // Timeout fallback
      setTimeout(function() {
        if (resolved) return;
        resolved = true;
        document.removeEventListener('ddm:availability:ready', onReady);
        console.warn('[PreCheckoutV2] DDMAvailability not loaded after ' + timeoutMs + 'ms');
        resolve(false);
      }, timeoutMs);
    });
  }

  // ==========================================================================
  // State Manager
  // ==========================================================================
  const StateManager = {
    storageKey: 'preCheckoutV2State',

    // The date an extra had pushed out, when this load brought it back (DD-MM-YYYY), or null
    keptDate: null,
    // The cart attributes the restored date came from (for the rate notices), or null
    _restoredStored: null,

    save() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          state: PreCheckoutV2.state
        }));
      } catch (e) {
        console.warn('[StateManager] Failed to save state:', e);
      }
    },

    readSession() {
      try {
        const data = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        return data && data.state ? data.state : null;
      } catch (e) {
        console.warn('[StateManager] Failed to read the session state:', e);
        return null;
      }
    },

    // Delivery attributes of the cart as the server rendered them on .pcv2-container
    // (written by Continue on an earlier visit). Inline like the availability payload,
    // so the start-up decision needs no request and cannot race one.
    storedAttributes() {
      const container = document.querySelector('.pcv2-container');
      const data = (container && container.dataset) || {};
      return {
        date: (data.storedDeliveryDate || '').trim(),
        rate: parseInt(data.storedDeliveryRate, 10) || 0,
        type: (data.storedDeliveryType || '').trim()
      };
    },

    /**
     * Decide the delivery date the page opens with. Synchronous, called once from init:
     * what the page shows selected is always what state holds and what Continue posts.
     * It reads only what Liquid rendered and what this tab remembers, so it runs the same
     * way after every reload - which is how the page learns about any cart change.
     *
     *   1. gift message + notes from this tab's session (never overwritten)
     *   2. option availability for the cart
     *   3. the date, in this order:
     *      - the date an upsell extra pushed out on an earlier load and the alert offered
     *        to keep ("Remove it to keep ..."), when the cart allows it again
     *      - the session date, when the cart still allows it (the latest choice)
     *      - else the cart attribute date, when the cart still allows it
     *      - an attribute date the cart no longer allows is not used: no option is
     *        selected and Continue asks for a date. The stale attributes stay on the
     *        cart until Continue's write replaces them; this page sends no clear of
     *        its own, because such a request could land after that write
     *      - a session-only date the cart no longer allows is replaced by the default
     *   4. with no date to restore, the default becomes a real selection: the first date
     *      the validator accepts, or none when the cart has no such date - then the
     *      no-date alert (ExtrasDateCheck.showNoFit) names the items and Continue goes on
     * A lost date is described as { date, reason, earliestDate, outcome, newDate }:
     *   outcome 'cleared':  it could not be kept, the shopper has to choose one
     *   outcome 'replaced': another date (newDate) is selected in its place
     */
    start() {
      const session = this.readSession();
      this.restoreGiftAndNotes(session);

      DeliveryOptionsModule.computeAvailability();

      const state = PreCheckoutV2.state;
      const stored = this.storedAttributes();
      const check = (dmy) => (dmy ? ValidationModule.checkDate(dmy) : null);
      let kept = (session && session.lostDate) || '';
      // The offer to keep that date stands while it is valid again or still pushed out
      // by extras alone; otherwise it is dropped (the shopper was told when it was lost)
      if (kept && !check(kept).valid && !ExtrasDateCheck.blockers(kept).length) kept = '';
      const sessionDate = (session && session.deliveryDate) || '';
      const wanted = kept || sessionDate; // what the shopper had in this tab
      const wantedCheck = check(wanted);
      const sessionCheck = check(sessionDate);
      const storedCheck = check(stored.date);
      let date = null;
      let lost = null;

      if (wantedCheck && wantedCheck.valid) {
        date = wanted;
      } else if (kept) {
        // Still pushed out: the page stays as the earlier load left it
        lost = Object.assign({ date: kept, outcome: 'cleared' }, wantedCheck);
        if (sessionCheck && sessionCheck.valid) {
          date = sessionDate;
          lost.outcome = 'replaced';
        }
      } else if (storedCheck && !storedCheck.valid) {
        lost = Object.assign({ date: stored.date, outcome: 'cleared' }, storedCheck);
      } else {
        if (wantedCheck) lost = Object.assign({ date: sessionDate, outcome: 'replaced' }, wantedCheck);
        if (storedCheck) date = stored.date;
      }

      if (date) {
        DeliveryOptionsModule.applyDate(date);
      } else if (lost && lost.outcome === 'cleared') {
        DeliveryOptionsModule.clearSelection();
      } else {
        DeliveryOptionsModule.selectDefault();
      }

      if (lost) {
        lost.newDate = state.deliveryDate;
        if (!lost.newDate) lost.outcome = 'cleared';
      }
      this.keptDate = kept && date === kept ? kept : null;
      this._restoredStored = date && date === stored.date ? stored : null;

      DeliveryOptionsModule.reprice();

      if (!state.deliveryDate && DeliveryOptionsModule.firstValidDate() === null) {
        // No day suits the whole cart: which items rule the days out, and that Continue
        // still goes through (our team arranges the date)
        ExtrasDateCheck.showNoFit();
      } else if (lost) {
        this.explainLost(lost);
      }
      this.rateNotices();
      console.log('[StateManager] Started with', state.deliveryDate, lost ? '(lost ' + lost.date + ': ' + lost.reason + ')' : '');
    },

    // The date check failed at start-up (no or unreadable availability data, or the check
    // threw). The date the shopper had - this tab's, else the cart's - stays only if the
    // clock alone allows it, with its type and price worked out from the date; Continue then
    // posts it marked unverified (CheckoutHandler.failOpen). Otherwise no date is shown, the
    // shopper is told, and Continue goes on as standard delivery, date to arrange.
    startUnchecked() {
      const session = this.readSession();
      const wanted = (session && session.deliveryDate) || this.storedAttributes().date;
      if (wanted && DeliveryOptionsModule.clockAllows(wanted)) {
        // No error: Continue works, and a refresh would render the same data
        DeliveryOptionsModule.applyDate(wanted);
      } else {
        DeliveryOptionsModule.clearSelection();
        NoticeManager.show('rate_changed', { message: CheckoutHandler.unconfirmedText() });
      }
      this.save();
    },

    // Never deliver on a date the shopper did not see: whenever the date they had could
    // not be kept, say what happened to it. The upsell extra is named when it alone is the
    // cause, with the offer to remove it - the date is then remembered (state.lostDate)
    // so the reload after that removal restores it. Otherwise the generic notice. Both
    // state the outcome: "choose a new date" when it was cleared, the date now applied
    // when it was replaced.
    explainLost(lost) {
      if (ExtrasDateCheck.showLost(lost.date)) {
        PreCheckoutV2.state.lostDate = lost.date;
        this.save();
      } else {
        NoticeManager.show('invalid', lost);
      }
    },

    // Rate-changed / cutoff notices for a date restored from the cart attributes
    rateNotices() {
      const stored = this._restoredStored;
      const state = PreCheckoutV2.state;
      if (!stored || state.deliveryDate !== stored.date) return;

      if (state.deliveryType.indexOf('same_day') === 0) {
        const minutesUntilCutoff = DateUtils.getMinutesUntilCutoff();
        if (minutesUntilCutoff <= 30) {
          NoticeManager.show('cutoff_warning', { minutesUntilCutoff: minutesUntilCutoff });
          return;
        }
      }
      if (stored.rate > 0 && state.deliveryPrice !== stored.rate) {
        const reason = ValidationModule.determineRateChangeReason(stored.rate, state.deliveryPrice, state.deliveryType);
        // "Standard delivery pricing now applies" only when the saved date was a peak date;
        // otherwise no peak period was involved: the new price, plainly
        if (reason === 'surge_ended' && stored.type.indexOf('surge') === -1) {
          NoticeManager.show('rate_changed', { message: NoticeManager.priceNow(state.deliveryPrice) });
        } else {
          NoticeManager.show('rate_changed', { changeReason: reason, price: state.deliveryPrice });
        }
      }
    },

    // Gift message and notes from the session copy into state and the form fields
    restoreGiftAndNotes(session) {
      if (!session) return;
      try {
        // A session saved before "untouched" was tracked: text in it was typed, and an
        // empty gift message or note only means that nothing had been typed
        const legacy = session.giftTouched === undefined;
        const gift = session.giftMessage || {};
        // The gift message as the shopper left it in this tab, an erased one too. An
        // untouched one stays what Liquid rendered: the cart's own.
        if (legacy ? (gift.greeting || gift.message || gift.signature) : session.giftTouched) {
          PreCheckoutV2.state.giftMessage = gift;
          PreCheckoutV2.state.giftTouched = true;
          GiftModule.write(gift);
          GiftModule.updateCharCounts();
          GiftModule.updateModalPreview();
          CardUpdater.updateGiftCard();
        }

        PreCheckoutV2.state.giftAttempted = !!session.giftAttempted;

        // An empty draft is a draft too: the shopper deleted the note
        if (typeof session.orderNotes === 'string' && (session.orderNotes || !legacy)) {
          PreCheckoutV2.state.orderNotes = session.orderNotes;
          NotesModule.restore();
        }
      } catch (e) {
        console.warn('[StateManager] Failed to restore gift/notes:', e);
      }
    }
  };

  // ==========================================================================
  // Date Utilities - Centralized date formatting and parsing
  // ==========================================================================
  const DateUtils = {
    // [CRO QW12 · S3A-05] Locale-aware short month/weekday names (FR calendar in FR funnel)
    MONTHS: (() => { const f = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short' }); return Array.from({ length: 12 }, (_, m) => f.format(new Date(2021, m, 15))); })(),
    DAYS: (() => { const f = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { weekday: 'short' }); return Array.from({ length: 7 }, (_, d) => f.format(new Date(2021, 7, 1 + d))); })(),

    /**
     * Format Date to ISO string (YYYY-MM-DD)
     * @param {Date} date
     * @returns {string}
     */
    toISO(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return y + '-' + m + '-' + d;
    },

    /**
     * Format Date to DMY string (DD-MM-YYYY) for storage
     * @param {Date} date
     * @returns {string}
     */
    toDMY(date) {
      const d = String(date.getDate()).padStart(2, '0');
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const y = date.getFullYear();
      return d + '-' + m + '-' + y;
    },

    /**
     * Format Date for display with weekday (Mon-DD, YYYY (Weekday))
     * @param {Date} date
     * @returns {string}
     */
    toDisplayWithWeekday(date) {
      return _dfDateWeekday.format(date);
    },

    /**
     * Format a DD-MM-YYYY date for use inside a sentence; the input itself if unparsable
     * @param {string} dmy
     * @returns {string}
     */
    toSentence(dmy) {
      const date = this.fromDMY(String(dmy || ''));
      if (!date) return String(dmy || '');
      const text = _dfDateSentence.format(date);
      // French writes the first of the month "1er" ("jeudi 1er octobre")
      return _PC_LANG.indexOf('fr') === 0 && date.getDate() === 1 ? text.replace(/(^|\s)1(\s)/, (match, before, after) => before + '1er' + after) : text;
    },

    /**
     * Parse DMY string (DD-MM-YYYY) to Date
     * @param {string} str
     * @returns {Date|null}
     */
    fromDMY(str) {
      const parts = str.split('-');
      if (parts.length !== 3) return null;
      const date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      return isNaN(date.getTime()) ? null : date;
    },

    /**
     * Strict DD-MM-YYYY that names a real day (what this page writes and can display)
     * @param {string} dmy
     * @returns {Date|null}
     */
    readDMY(dmy) {
      const parsed = /^\d{2}-\d{2}-\d{4}$/.test(dmy || '') ? this.fromDMY(dmy) : null;
      return parsed && this.toDMY(parsed) === dmy ? parsed : null;
    },

    /**
     * Parse ISO string (YYYY-MM-DD) to Date
     * @param {string} str
     * @returns {Date|null}
     */
    fromISO(str) {
      const parts = str.split('-');
      if (parts.length !== 3) return null;
      const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return isNaN(date.getTime()) ? null : date;
    },

    /**
     * Get current date in GMT+4 (Mauritius) timezone
     * @returns {Date}
     */
    getGMT4Today() {
      const now = new Date();
      const { tzOffsetHours } = PreCheckoutV2.config;
      const totalOffsetMinutes = now.getTimezoneOffset() + (tzOffsetHours * 60);
      const gmt4Now = new Date(now.getTime() + (totalOffsetMinutes * 60 * 1000));
      return new Date(gmt4Now.getFullYear(), gmt4Now.getMonth(), gmt4Now.getDate());
    },

    /**
     * Check if two dates are the same day
     * @param {Date} d1
     * @param {Date} d2
     * @returns {boolean}
     */
    isSameDay(d1, d2) {
      return d1.getFullYear() === d2.getFullYear() &&
             d1.getMonth() === d2.getMonth() &&
             d1.getDate() === d2.getDate();
    },

    /**
     * Get minutes until cutoff time in GMT+4
     * @returns {number}
     */
    getMinutesUntilCutoff() {
      const now = new Date();
      const { tzOffsetHours, cutOffHour, cutOffMinute } = PreCheckoutV2.config;
      const totalOffsetMinutes = now.getTimezoneOffset() + (tzOffsetHours * 60);
      const gmt4Now = new Date(now.getTime() + (totalOffsetMinutes * 60 * 1000));
      // Compare minutes numerically - avoids setHours() date-boundary bug
      const currentMinutes = gmt4Now.getHours() * 60 + gmt4Now.getMinutes();
      const cutoffMinutes = cutOffHour * 60 + cutOffMinute;
      return Math.max(0, cutoffMinutes - currentMinutes);
    }
  };

  // ==========================================================================
  // Validation Module - Cart Attribute Date Validation
  // ==========================================================================
  const ValidationModule = {
    /**
     * Can this date (DD-MM-YYYY) be delivered for the current cart?
     * Delegates to DDMAvailability.validateCartDeliveryDate, the helper the drawer uses
     * too, so both surfaces apply one rule set in one order (weekly closures before the
     * generic blocked-date check, which is what makes the closed-day wording reachable).
     * Never "valid" without the data: no availability module, or a payload that is missing
     * or unreadable (malformed JSON, no items), is a failed check - it throws, and the
     * callers fail open (StateManager.startUnchecked, CheckoutHandler.failOpen).
     * @returns {{valid: boolean, reason: string|null, earliestDate: string}}
     */
    checkDate(dmy) {
      // Only DD-MM-YYYY that names a real day: what this page writes and can display.
      // The shared helper also reads other shapes (ISO); here they are unreadable.
      if (!DateUtils.readDMY(dmy)) return { valid: false, reason: 'date_invalid_generic', earliestDate: '' };
      const DDM = window.DDMAvailability;
      const payload = DDM && typeof DDM.validateCartDeliveryDate === 'function'
        ? DDM.parseJSONScript('pcv2-availability-config')
        : null;
      if (!payload || !Array.isArray(payload.items)) throw new Error('availability data missing or unreadable');
      const result = DDM.validateCartDeliveryDate(payload, dmy, new Date(), {
        tzOffsetHours: PreCheckoutV2.config.tzOffsetHours
      });
      return {
        valid: result.valid,
        reason: result.reason,
        earliestDate: result.earliestISO ? DateUtils.toSentence(result.earliestISO.split('-').reverse().join('-')) : ''
      };
    },

    /**
     * Determine the reason for rate change
     */
    determineRateChangeReason(oldRate, newRate, deliveryType) {
      const isPrime = PreCheckoutV2.isPrimeMember;
      const isLegacyPrime = document.querySelector('[data-is-legacy-prime="true"]') !== null;
      const hasGifts = document.querySelector('[data-cart-has-gifts="true"]') !== null;
      const isSurge = deliveryType.includes('surge');

      // FREE unlocked
      if (newRate === 0 && oldRate > 0) return 'free_unlocked';
      // Prime applied (rate decreased + is prime)
      if (newRate < oldRate && isPrime) return 'prime_applied';
      // Legacy Prime + Gifts
      if (newRate < oldRate && isLegacyPrime && hasGifts) return 'legacy_activated';
      // Surge ended
      if (newRate < oldRate && !isSurge) return 'surge_ended';
      // Surge started
      if (newRate > oldRate && isSurge) return 'surge_started';
      // Generic rate update
      return 'rate_updated';
    }
  };

  // ==========================================================================
  // Notice Manager - Validation Notices (uses safe DOM methods)
  // ==========================================================================
  const NoticeManager = {
    // Two places, so that no notice moves the delivery options once the page is in use:
    //   inline - 'invalid': the date the shopper had could not be kept. Decided
    //            synchronously at start-up, so it is in the card from the first paint,
    //            and it stays until dismissed or a date is picked (no timer, so nothing
    //            jumps back 8 s later either).
    //   toast  - everything else (rate changed, cutoff warning, error). Overlays the
    //            page above the sticky bar and dismisses itself.
    // The one toast holds up to two sentences, one of each kind, so that neither hides the
    // other: a cart message (what just happened: 'info', 'error') first, then a selection
    // warning (SELECTION_TYPES) that still applies. A new sentence replaces only the one of
    // its own kind; a real change of the selection takes only the selection sentence away.
    inline: null,
    toast: null,
    toastParts: null, // { cart: part | null, selection: part | null }, part = { type, text, silent }
    dismissTimer: null,
    AUTO_DISMISS_MS: 8000,
    // Toasts about the terms of the selection: its price (P8 / P12, standard and peak), the
    // "Press Continue to confirm" stop, the date to arrange, the same-day cutoff, the date
    // an extra had pushed out and that is back ('date_kept', shown like 'info'). A change of
    // the selected date or type makes them wrong (dropSelectionNotices). Errors and cart
    // reports ('error', 'info') are about other things and stay.
    SELECTION_TYPES: ['rate_changed', 'cutoff_warning', 'date_kept'],

    /**
     * Show validation notice
     * @param {string} noticeType - 'invalid' | 'rate_changed' | 'cutoff_warning' | 'error' | 'info'
     * @param {Object} data - 'invalid': { date, reason, earliestDate, outcome, newDate };
     *   any type: { message } to say exactly that, { silent: true } when the text is
     *   announced elsewhere (the status region) and the notice is only its visible copy
     */
    show(noticeType, data) {
      data = data || {};
      if (noticeType === 'invalid') {
        const container = document.querySelector('[data-pcv2-delivery-options]');
        if (!container) return;
        this.hideInline();
        const notice = this.build(noticeType, data, true);
        container.parentNode.insertBefore(notice, container);
        this.inline = notice;
        return;
      }

      const part = { type: noticeType, text: this.getNoticeMessage(noticeType, data), silent: !!data.silent };
      const parts = Object.assign({ cart: null, selection: null }, this.toastParts);
      parts[this.SELECTION_TYPES.indexOf(noticeType) !== -1 ? 'selection' : 'cart'] = part;
      this.renderToast(parts);
    },

    // The toast for these parts (see toastParts): the cart sentence first. It looks like an
    // error when either part is one; it is announced unless its only sentence is a silent one
    // (said in the status region already).
    renderToast(parts) {
      this.hideToast();
      const shown = [parts.cart, parts.selection].filter(Boolean);
      if (!shown.length) return;
      const isError = shown.some((part) => part.type === 'error');
      const lead = (parts.selection || parts.cart).type;
      const type = isError ? 'error' : (lead === 'date_kept' ? 'info' : lead);
      const notice = this.build(type, {
        message: shown.map((part) => part.text).join(' '),
        silent: shown.every((part) => part.silent)
      }, false);
      notice.classList.add('pcv2-validation-notice--toast');
      (document.querySelector('.pre-checkout-v2') || document.body).appendChild(notice);
      this.toast = notice;
      this.toastParts = parts;
      this.dismissTimer = setTimeout(() => this.hideToast(), this.AUTO_DISMISS_MS);
    },

    build(noticeType, data, isInline) {
      const notice = document.createElement('div');
      notice.className = 'pcv2-validation-notice pcv2-validation-notice--' + noticeType;
      // alert: the shopper was overruled or must act. status: information only.
      // (Not role="alert" plus aria-live="polite": the two contradict each other.)
      if (!data.silent) notice.setAttribute('role', noticeType === 'invalid' || noticeType === 'error' ? 'alert' : 'status');
      notice.setAttribute('data-pcv2-notice', '');

      // Build notice using safe DOM methods
      const iconEl = this.createIcon(noticeType);
      // setAttribute: SVGElement.className is read-only, and assigning it throws in strict mode
      iconEl.setAttribute('class', 'pcv2-notice__icon');
      iconEl.setAttribute('aria-hidden', 'true');

      const messageEl = document.createElement('div');
      messageEl.className = 'pcv2-notice__message';
      messageEl.setAttribute('data-pcv2-notice-text', '');
      messageEl.textContent = this.getNoticeMessage(noticeType, data);

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'pcv2-notice__close';
      closeBtn.setAttribute('aria-label', EX_TXT.dismiss);
      closeBtn.setAttribute('data-pcv2-notice-close', '');
      closeBtn.appendChild(this.createCloseIcon());
      closeBtn.addEventListener('click', () => (isInline ? this.hideInline() : this.hideToast()));

      notice.appendChild(iconEl);
      notice.appendChild(messageEl);
      notice.appendChild(closeBtn);
      return notice;
    },

    createIcon(noticeType) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('fill', 'currentColor');

      svg.setAttribute('data-pcv2-notice-icon', noticeType);
      svg.appendChild(this.iconPath(noticeType));
      return svg;
    },

    iconPath(noticeType) {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill-rule', 'evenodd');

      if (noticeType === 'resolved') {
        // Check circle (a date is selected)
        path.setAttribute('d', 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z');
      } else if (noticeType === 'invalid') {
        // Warning triangle
        path.setAttribute('d', 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z');
      } else if (noticeType === 'rate_changed' || noticeType === 'info') {
        // Info circle
        path.setAttribute('d', 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z');
      } else if (noticeType === 'error') {
        // X circle (error)
        path.setAttribute('d', 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z');
      } else {
        // Clock
        path.setAttribute('d', 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z');
      }
      return path;
    },

    createCloseIcon() {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '16');
      svg.setAttribute('height', '16');
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('fill', 'currentColor');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z');

      svg.appendChild(path);
      return svg;
    },

    getNoticeMessage(noticeType, data) {
      const lang = document.documentElement.lang || 'en';
      const isFr = lang.startsWith('fr');

      if (data.message) return data.message;
      if (noticeType === 'invalid') {
        // Why the date went + what happened to it (cleared or replaced)
        return this.getInvalidMessage(data.reason, data, isFr) + ' ' + this.getOutcomeMessage(data, isFr);
      } else if (noticeType === 'rate_changed') {
        return this.getRateChangeMessage(data.changeReason, isFr, data.price);
      } else if (noticeType === 'cutoff_warning') {
        return isFr
          ? 'La livraison le jour même se termine dans ' + data.minutesUntilCutoff + ' minutes. Finalisez votre commande pour être livré aujourd\'hui.'
          : 'Same-day cutoff in ' + data.minutesUntilCutoff + ' minutes. Complete checkout to guarantee today\'s delivery.';
      } else if (noticeType === 'error') {
        // The date check failed and not even the clock-only fallback could keep a date
        // (PreCheckoutV2.init). No "refresh": a refresh renders the same data.
        return isFr
          ? 'Nous n\'avons pas pu vérifier vos options de livraison. Vous pouvez continuer : notre équipe confirmera votre livraison.'
          : 'We couldn\'t check your delivery options. You can still continue: our team will confirm your delivery.';
      }
      return '';
    },

    // Why the date could not be kept. One sentence, no instruction: what happens next is
    // getOutcomeMessage()'s job, because it differs (cleared vs replaced).
    getInvalidMessage(reason, data, isFr) {
      const date = DateUtils.toSentence(data.date);
      const messages = {
        date_passed: isFr
          ? 'Le ' + date + ' est passé.'
          : date + ' has passed.',
        cutoff_passed: isFr
          ? 'La livraison le jour même est terminée pour aujourd\'hui.'
          : 'Same-day delivery has closed for today.',
        // [RELEASE B] Emitted by DDMAvailability.validateCartDeliveryDate when policy
        // disables same-day outright (sameDayConfig.enabled === false), as distinct
        // from cutoff_passed which means the clock ran out. Without this key the
        // shared helper's reason fell through to the generic message below.
        same_day_disabled: isFr
          ? 'La livraison le jour même n\'est pas disponible pour les articles de votre panier.'
          : 'Same-day delivery is not available for the items in your cart.',
        // Weekly closures come from the vertical (DodoFresh, DodoBaby: Monday to Friday)
        // or from a vendor, so the sentence names the day, not a vertical
        weekly_closed: isFr
          ? 'Le ' + date + ' n\'est pas un jour de livraison pour certains articles de votre panier.'
          : date + ' is not a delivery day for some items in your cart.',
        date_blocked: isFr
          ? 'Le ' + date + ' n\'est plus disponible pour votre panier.'
          : date + ' is no longer available for your cart.',
        lead_time_conflict: isFr
          ? 'Votre panier nécessite désormais plus de temps de préparation que ne le permet le ' + date + '.'
          : 'Your cart now needs more preparation time than ' + date + ' allows.',
        date_invalid_generic: isFr
          ? 'La date précédemment sélectionnée n\'est plus disponible.'
          : 'Your previously selected date is no longer available.'
      };
      return messages[reason] || messages.date_invalid_generic;
    },

    // What happened to the selection: another date is now applied (say which), or none is
    getOutcomeMessage(data, isFr) {
      if (data.outcome === 'replaced' && data.newDate) {
        const newDate = DateUtils.toSentence(data.newDate);
        return isFr
          ? 'La livraison est maintenant prévue le ' + newDate + '. Vous pouvez choisir une autre date.'
          : 'Delivery is now set to ' + newDate + '. You can choose another date.';
      }
      if (data.reason === 'lead_time_conflict' && data.earliestDate) {
        return isFr
          ? 'Veuillez choisir une date à partir du ' + data.earliestDate + '.'
          : 'Please choose a date on or after ' + data.earliestDate + '.';
      }
      return isFr
        ? 'Veuillez choisir une nouvelle date de livraison.'
        : 'Please choose a new delivery date.';
    },

    // "Your delivery price is now Rs 599." - with "Press Continue to confirm." when it is
    // what stopped Continue
    priceNow(cents, confirm) {
      const isFr = _PC_LANG.indexOf('fr') === 0;
      let text;
      if (cents === 0) {
        text = isFr ? 'Votre livraison est maintenant gratuite.' : 'Your delivery is now free.';
      } else {
        text = isFr ? 'Le prix de livraison est maintenant de ' + this.priceInText(cents, isFr) + '.' : 'Your delivery price is now ' + this.priceInText(cents, isFr) + '.';
      }
      if (confirm) text += isFr ? ' Appuyez sur Continuer pour confirmer.' : ' Press Continue to confirm.';
      return text;
    },

    // A price inside a sentence: "Rs" and the amount joined by a non-breaking space so they
    // never wrap apart; "free" / "gratuite" for 0
    priceInText(cents, isFr) {
      if (cents === 0) return isFr ? 'gratuite' : 'free';
      return fmt(cents).replace(' ', ' ');
    },

    // The peak notices name the price when it is given ("...: Rs 599."; FR with a
    // non-breaking space before the colon, French typography)
    getRateChangeMessage(reason, isFr, price) {
      const priced = typeof price === 'number';
      const withPrice = (text, colon) => (priced ? text + colon + this.priceInText(price, isFr) + '.' : text + '.');
      const messages = {
        prime_applied: isFr
          ? 'Vos avantages de livraison DodoPrime ont été appliqués.'
          : 'Your DodoPrime delivery benefits have been applied.',
        prime_added: isFr
          ? 'DodoPrime ajouté : vos avantages de livraison sont actifs !'
          : 'DodoPrime added: delivery benefits are now active!',
        free_unlocked: isFr
          ? 'Livraison standard GRATUITE débloquée !'
          : 'FREE standard delivery unlocked!',
        legacy_activated: isFr
          ? 'Vos avantages DodoPrime ont été appliqués à cette commande.'
          : 'Your DodoPrime benefits have been applied to this order.',
        surge_ended: isFr
          ? withPrice('Le tarif de livraison standard s\'applique désormais', ' : ')
          : withPrice('Standard delivery pricing now applies', ': '),
        surge_started: isFr
          ? withPrice('Le tarif de haute saison s\'applique à la date sélectionnée', ' : ')
          : withPrice('Peak period pricing applies to your selected date', ': '),
        rate_updated: isFr
          ? 'Votre tarif de livraison a été mis à jour.'
          : 'Your delivery rate has been updated.'
      };
      return messages[reason] || messages.rate_updated;
    },

    hideInline() {
      if (this.inline) {
        this.inline.remove();
        this.inline = null;
      }
    },

    // The shopper answered it by picking a date. The box stays where it is, but becomes a
    // calm confirmation of what is true now - the date selected, with a check icon - and
    // shrinks to that one line (a small shift, after the shopper's own action). Nothing
    // it offered is left in it. With no date to state, it goes. Used for the inline
    // notice and the extras alert.
    resolve(box, text) {
      const content = box.querySelector('[data-pcv2-notice-text]');
      if (!text || !content) {
        box.remove();
        return false;
      }
      content.textContent = text;
      const icon = box.querySelector('[data-pcv2-notice-icon]');
      if (icon) {
        icon.textContent = '';
        icon.appendChild(this.iconPath('resolved'));
        icon.setAttribute('data-pcv2-notice-icon', 'resolved');
      }
      box.classList.add('pcv2-validation-notice--resolved');
      box.setAttribute('role', 'status');
      return true;
    },

    resolveInline(text) {
      if (this.inline && !this.resolve(this.inline, text)) this.inline = null;
    },

    // The selection's terms (date, type, rate), to tell a real change from a repeat
    selectionKey() {
      const state = PreCheckoutV2.state;
      return state.deliveryDate + '|' + state.deliveryType + '|' + state.deliveryPrice;
    },

    // The selected date, type or rate changed (from `before`, a selectionKey()): a toast
    // about the old terms would now be wrong. A re-tap or re-confirm of the same selection
    // changes nothing and keeps notices that are still true (the same-day cutoff, say).
    dropSelectionNotices(before) {
      if (before === this.selectionKey()) return;
      CartSync.dropKeptStatus(); // the status region too, also when the toast is gone already
      if (this.toast && this.toastParts && this.toastParts.selection) {
        this.renderToast({ cart: this.toastParts.cart, selection: null }); // the cart message stays
      }
    },

    // Continue was pressed: the cart message ("press Continue", a failed save) has done its
    // job; a selection warning stays
    dropCartNotice() {
      if (this.toast && this.toastParts && this.toastParts.cart) {
        this.renderToast({ cart: null, selection: this.toastParts.selection });
      }
    },

    hideToast() {
      if (this.toast) {
        this.toast.remove();
        this.toast = null;
      }
      this.toastParts = null;
      if (this.dismissTimer) {
        clearTimeout(this.dismissTimer);
        this.dismissTimer = null;
      }
    }
  };

  // ==========================================================================
  // Modal Controller
  // ==========================================================================
  const ModalController = {
    activeModal: null,
    previousFocus: null,
    _focusableElements: null,
    _boundElements: [], // Track elements with bound handlers for cleanup

    cleanup() {
      // Remove all previously bound listeners
      this._boundElements.forEach(({ el, event, handler }) => {
        el.removeEventListener(event, handler);
      });
      this._boundElements = [];
    },

    init() {
      // Clean up existing listeners before re-binding (prevents memory leaks on section reload)
      this.cleanup();

      // Open modal buttons
      document.querySelectorAll('[data-pcv2-open-modal]').forEach(btn => {
        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const modalId = btn.dataset.pcv2OpenModal;
          this.open(modalId, btn);
        };
        btn.addEventListener('click', handler);
        this._boundElements.push({ el: btn, event: 'click', handler });
      });

      // Make entire cards clickable (except delivery-v3 which has its own option handlers)
      document.querySelectorAll('[data-pcv2-card]').forEach(card => {
        const handler = (e) => {
          // Don't trigger if clicking on a button inside
          if (e.target.closest('button')) return;
          // Don't trigger for delivery-v3 card - it has its own radio option handlers
          if (e.target.closest('[data-pcv2-option]')) return;
          const cardType = card.dataset.pcv2Card;
          const btn = card.querySelector('[data-pcv2-open-modal]');
          this.open(cardType, btn || card);
        };
        card.addEventListener('click', handler);
        this._boundElements.push({ el: card, event: 'click', handler });
      });

      // Close modal buttons (X button, backdrop, cancel)
      document.querySelectorAll('[data-pcv2-modal-close]').forEach(btn => {
        const handler = (e) => {
          e.preventDefault();
          this.close();
        };
        btn.addEventListener('click', handler);
        this._boundElements.push({ el: btn, event: 'click', handler });
      });

      // Save buttons
      document.querySelectorAll('[data-pcv2-modal-save]').forEach(btn => {
        const handler = (e) => {
          e.preventDefault();
          const modalType = btn.dataset.pcv2ModalSave;
          this.saveAndClose(modalType);
        };
        btn.addEventListener('click', handler);
        this._boundElements.push({ el: btn, event: 'click', handler });
      });

      // Erase buttons (gift/notes modals)
      document.querySelectorAll('[data-pcv2-modal-erase]').forEach(btn => {
        const handler = (e) => {
          e.preventDefault();
          const modalType = btn.dataset.pcv2ModalErase;
          this.erase(modalType);
        };
        btn.addEventListener('click', handler);
        this._boundElements.push({ el: btn, event: 'click', handler });
      });
    },

    open(modalId, triggerEl) {
      // The page is locked (a cart request or a reload is under way): no edits
      if (CartSync.busy) return;
      CartSync._staleReloads = 0; // the shopper acts on the page: count from zero again
      const modal = document.getElementById('pcv2-modal-' + modalId);
      if (!modal) return;

      console.log('[ModalController] Opening modal:', modalId);

      // Store previous focus
      this.previousFocus = triggerEl || document.activeElement;
      this.activeModal = modal;

      // Show modal
      modal.classList.add('is-open');
      document.body.classList.add('pcv2-modal-open');

      // Initialize modal content
      if (modalId === 'delivery') {
        CalendarModule.render();
        // Discard any un-confirmed tap from a previous open; reflect the committed date
        CalendarModule.syncToCommitted();
      }

      // Setup focus trap
      setTimeout(() => {
        this._focusableElements = modal.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (this._focusableElements.length) {
          this._focusableElements[0].focus();
        }
      }, 100);

      // Bind keyboard handlers
      this.bindEscape();
      this.bindFocusTrap();
    },

    close() {
      if (!this.activeModal) return;

      console.log('[ModalController] Closing modal');

      this.activeModal.classList.remove('is-open');
      document.body.classList.remove('pcv2-modal-open');

      // Restore focus
      if (this.previousFocus) {
        this.previousFocus.focus();
      }

      this.activeModal = null;
      this._focusableElements = null;
      this.unbindEscape();
      this.unbindFocusTrap();
    },

    saveAndClose(modalType) {
      console.log('[ModalController] Save and close:', modalType);

      if (modalType === 'delivery') {
        // Commit the pending date to the order (transactional — nothing committed until Confirm)
        if (!CalendarModule.commitPendingDate()) {
          this.showError('delivery', PreCheckoutV2.config.typeLabels.error_select_date);
          return;
        }
        this.clearError('delivery');
        CardUpdater.updateDeliveryCard();
        SidebarUpdater.update();
        StateManager.save();
        // Update V3 radio cards if present
        if (typeof DeliveryOptionsModule !== 'undefined') {
          DeliveryOptionsModule.handleCalendarSelection();
        }
        // [UPSELL S5] A new date is chosen: the "date lost" alert is moot
        ExtrasDateCheck.clearLost();
      }

      if (modalType === 'gift') {
        CardUpdater.updateGiftCard();
        StateManager.save();
      }

      if (modalType === 'notes') {
        CardUpdater.updateNotesCard();
        StateManager.save();
      }

      this.close();
    },

    erase(modalType) {
      if (modalType === 'gift') {
        this.eraseGiftMessage();
      } else if (modalType === 'notes') {
        this.eraseNotes();
      }
    },

    eraseGiftMessage() {
      console.log('[ModalController] Erasing gift message');

      // Clear state
      PreCheckoutV2.state.giftMessage = {
        occasion: '',
        greeting: '',
        message: '',
        signature: ''
      };

      // Clear form inputs
      const occasionSelect = document.querySelector('[data-pcv2-gift-occasion]');
      const greetingInput = document.querySelector('[data-pcv2-gift-greeting]');
      const messageInput = document.querySelector('[data-pcv2-gift-message]');
      const signatureInput = document.querySelector('[data-pcv2-gift-signature]');

      if (occasionSelect) occasionSelect.value = '';
      if (greetingInput) greetingInput.value = '';
      if (messageInput) messageInput.value = '';
      if (signatureInput) signatureInput.value = '';

      // Update char counts
      GiftModule.updateCharCounts();

      // Hide modal preview
      GiftModule.updateModalPreview();

      // Update card (hide preview)
      CardUpdater.updateGiftCard();

      // An erase is an edit: marked and saved
      GiftModule.touch();

      // Close modal
      this.close();
    },

    eraseNotes() {
      console.log('[ModalController] Erasing notes');

      // Clear state
      PreCheckoutV2.state.orderNotes = '';

      // Clear form input
      const textarea = document.querySelector('[data-pcv2-notes-input]');
      if (textarea) textarea.value = '';

      // Update char count
      NotesModule.updateCharCount();

      // Update card (hide preview)
      CardUpdater.updateNotesCard();

      // Save state
      StateManager.save();

      // Close modal
      this.close();
    },

    bindEscape() {
      this._escapeHandler = (e) => {
        if (e.key === 'Escape') this.close();
      };
      document.addEventListener('keydown', this._escapeHandler);
    },

    unbindEscape() {
      if (this._escapeHandler) {
        document.removeEventListener('keydown', this._escapeHandler);
      }
    },

    bindFocusTrap() {
      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab' || !this._focusableElements || !this._focusableElements.length) return;

        const firstEl = this._focusableElements[0];
        const lastEl = this._focusableElements[this._focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: if on first element, go to last
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          // Tab: if on last element, go to first
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      };
      document.addEventListener('keydown', this._focusTrapHandler);
    },

    unbindFocusTrap() {
      if (this._focusTrapHandler) {
        document.removeEventListener('keydown', this._focusTrapHandler);
      }
    },

    showError(modalType, message) {
      const modal = document.getElementById('pcv2-modal-' + modalType);
      if (!modal) return;

      let errorEl = modal.querySelector('[data-pcv2-modal-error]');
      if (!errorEl) {
        // Create error element if it doesn't exist
        errorEl = document.createElement('div');
        errorEl.className = 'pcv2-modal__error';
        errorEl.setAttribute('data-pcv2-modal-error', '');
        errorEl.setAttribute('role', 'alert');
        errorEl.setAttribute('aria-live', 'polite');
        const body = modal.querySelector('.pcv2-modal__body');
        if (body) body.insertBefore(errorEl, body.firstChild);
      }

      errorEl.textContent = message;
      errorEl.style.display = 'block';
    },

    clearError(modalType) {
      const modal = document.getElementById('pcv2-modal-' + modalType);
      if (!modal) return;

      const errorEl = modal.querySelector('[data-pcv2-modal-error]');
      if (errorEl) {
        errorEl.style.display = 'none';
        errorEl.textContent = '';
      }
    }
  };

  // ==========================================================================
  // Calendar Module (Uses DDMAvailability for date computation)
  // ==========================================================================
  const CalendarModule = {
    rendered: false,
    _disabledSet: null,
    _enabledDates: null,  // [DODOFRESH] Whitelist from allowed_dates_range
    _surgeRanges: null,
    _weeklyClosedDays: null,
    _clickHandler: null,

    init() {
      // Calendar is rendered on modal open
    },

    /**
     * Compute disabled dates using DDMAvailability module
     * Reads from pcv2-availability-config JSON payload
     * @returns {Set} Set of disabled ISO date strings
     */
    computeDisabledDates() {
      if (!window.DDMAvailability) {
        console.warn('[CalendarModule] DDMAvailability not loaded');
        return new Set();
      }

      const payload = window.DDMAvailability.parseJSONScript('pcv2-availability-config');
      if (!payload || !payload.items || !payload.items.length) {
        return new Set();
      }

      // [DODOFRESH] Union per-item weeklyClosedDays (vertical ∪ vendor per item)
      var weeklyClosedDays;
      if (window.DDMAvailability.unionItemWeeklyClosedDays) {
        weeklyClosedDays = window.DDMAvailability.unionItemWeeklyClosedDays(payload.items);
      } else {
        // Fallback to top-level (backwards compat)
        weeklyClosedDays = payload.weeklyClosedDays || [];
      }
      const result = window.DDMAvailability.computeCartAvailability(payload.items, {
        weeklyClosedDays: weeklyClosedDays
      });

      // Store weeklyClosedDays for calendar labeling
      this._weeklyClosedDays = weeklyClosedDays;

      // [DODOFRESH] Store enabledDates (whitelist) if present
      this._enabledDates = result.enabledDates || null;

      return new Set(result.disabledDates || []);
    },

    /**
     * Get weeklyClosedDays from availability config
     * @returns {Array<number>} Array of day-of-week numbers (0=Sun, 6=Sat)
     */
    getWeeklyClosedDays() {
      if (this._weeklyClosedDays !== null) return this._weeklyClosedDays;
      const payload = window.DDMAvailability ? window.DDMAvailability.parseJSONScript('pcv2-availability-config') : null;
      this._weeklyClosedDays = payload ? (payload.weeklyClosedDays || []) : [];
      return this._weeklyClosedDays;
    },

    /**
     * Check if a date falls on a weekly closed day
     * @param {Date|string} date - Date object or ISO string
     * @returns {boolean} True if date is on a closed day
     */
    isWeeklyClosedDay(date) {
      const closedDays = this.getWeeklyClosedDays();
      if (!closedDays.length) return false;
      if (window.DDMAvailability && window.DDMAvailability.isWeeklyClosedDay) {
        return window.DDMAvailability.isWeeklyClosedDay(date, closedDays);
      }
      // Fallback: inline check
      var d = (date instanceof Date) ? date : new Date(date + 'T00:00:00');
      return closedDays.indexOf(d.getDay()) !== -1;
    },

    /**
     * Get surge date ranges from availability config
     * @returns {Array} Array of {start, end} objects
     */
    getSurgeRanges() {
      if (!window.DDMAvailability) return [];

      const payload = window.DDMAvailability.parseJSONScript('pcv2-availability-config');
      return payload ? (payload.surgeRanges || []) : [];
    },

    /**
     * Check if date falls within any surge range
     * @param {string} dateStr - ISO date string (YYYY-MM-DD)
     * @param {Array} ranges - Array of {start, end} objects
     * @returns {boolean}
     */
    isInSurgeRange(dateStr, ranges) {
      if (!ranges || !ranges.length) return false;
      return ranges.some(r => dateStr >= r.start && dateStr <= r.end);
    },

    /**
     * Get lead time configuration from availability config
     * Returns number of days to offset minimum date
     * @returns {number} Lead time in days
     */
    getLeadTimeDays() {
      if (!window.DDMAvailability) return 0;
      const payload = window.DDMAvailability.parseJSONScript('pcv2-availability-config');
      if (!payload || !payload.sameDayConfig) return 0;
      const maxLeadHours = payload.sameDayConfig.maxLeadHours || 0;
      // Convert hours to days (round up)
      return Math.ceil(maxLeadHours / 24);
    },

    /**
     * Calculate minimum selectable date based on cutoff and lead time
     * @returns {Date} Minimum date
     */
    getMinDate() {
      const today = DateUtils.getGMT4Today();
      const leadDays = this.getLeadTimeDays();
      const isSameDayAvailable = this.checkSameDayAvailable();

      // Start with today or tomorrow based on cutoff
      let minDate = new Date(today);
      if (!isSameDayAvailable) {
        minDate.setDate(minDate.getDate() + 1);
      }

      // Add lead time days
      if (leadDays > 0) {
        minDate.setDate(minDate.getDate() + leadDays);
      }

      return minDate;
    },

    render() {
      const calendarEl = document.getElementById('pcv2DeliveryCalendar');
      if (!calendarEl) return;
      if (this.rendered) return;

      console.log('[CalendarModule] Rendering calendar...');

      // Compute disabled dates from DDMAvailability (metaobject-driven)
      this._disabledSet = this.computeDisabledDates();
      this._surgeRanges = this.getSurgeRanges();

      const today = DateUtils.getGMT4Today();
      const isSameDayAvailable = this.checkSameDayAvailable();
      const minDate = this.getMinDate();

      // Read config for calendar label logic
      var maxLeadHours = 0;
      var hasAllowedDatesRange = false;
      try {
        var configEl = document.getElementById('pcv2-availability-config');
        if (configEl) {
          var payload = JSON.parse(configEl.textContent);
          if (payload) {
            maxLeadHours = (payload.sameDayConfig && payload.sameDayConfig.maxLeadHours) || 0;
            hasAllowedDatesRange = payload.hasAllowedDatesRange || false;
          }
        }
      } catch (e) {
        console.warn('[CalendarModule] Failed to read config for labels:', e);
      }

      console.log('[CalendarModule] Lead time days:', this.getLeadTimeDays(), 'Min date:', minDate.toISOString().split('T')[0]);

      // Render 4 months
      const fragment = document.createDocumentFragment();
      const weeklyClosedDays = this.getWeeklyClosedDays();
      // [DODOFRESH] Convert enabledDates array to Set for O(1) lookups
      const enabledSet = this._enabledDates ? new Set(this._enabledDates) : null;
      for (let i = 0; i < 4; i++) {
        const monthDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
        const monthEl = this.renderMonth(monthDate, {
          today,
          minDate,
          isSameDayAvailable,
          disabledSet: this._disabledSet,
          enabledSet: enabledSet,  // [DODOFRESH] Whitelist set
          surgeRanges: this._surgeRanges,
          maxLeadHours: maxLeadHours,
          hasAllowedDatesRange: hasAllowedDatesRange,
          weeklyClosedDays: weeklyClosedDays
        });
        fragment.appendChild(monthEl);
      }

      // Clear and append
      const inputs = calendarEl.querySelectorAll('input[type="hidden"]');
      calendarEl.textContent = '';
      inputs.forEach(input => calendarEl.appendChild(input));
      calendarEl.appendChild(fragment);

      // Remove old click handler if exists (prevents listener accumulation)
      if (this._clickHandler) {
        calendarEl.removeEventListener('click', this._clickHandler);
      }

      // Add click handlers
      this._clickHandler = (e) => {
        const dayEl = e.target.closest('.calendar-day');
        if (!dayEl) return;
        if (dayEl.classList.contains('calendar-day--disabled') || dayEl.classList.contains('calendar-day--empty')) return;

        const dateStr = dayEl.dataset.date;
        if (!dateStr) return;

        // Remove previous selection
        const prevSelected = calendarEl.querySelector('.calendar-day--selected');
        if (prevSelected) prevSelected.classList.remove('calendar-day--selected');

        // Add selection
        dayEl.classList.add('calendar-day--selected');

        // Handle selection
        const parts = dateStr.split('-');
        const selectedDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        const formattedDate = parts[2] + '-' + parts[1] + '-' + parts[0]; // DD-MM-YYYY
        this.handleDateSelection(selectedDate, formattedDate);
      };
      calendarEl.addEventListener('click', this._clickHandler);

      // Restore selection if exists
      if (PreCheckoutV2.state.deliveryDate) {
        const parts = PreCheckoutV2.state.deliveryDate.split('-');
        const isoDate = parts[2] + '-' + parts[1].padStart(2, '0') + '-' + parts[0].padStart(2, '0'); // YYYY-MM-DD
        const dayEl = calendarEl.querySelector(`[data-date="${isoDate}"]`);
        if (dayEl) dayEl.classList.add('calendar-day--selected');
      }

      this.rendered = true;
    },

    renderMonth(monthDate, opts) {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();

      // [CRO QW12 · S3A-05] Reuse locale-aware names; weekday row stays 3-letter uppercase
      const monthNames = DateUtils.MONTHS;
      const weekdays = DateUtils.DAYS.map(d => d.toUpperCase().replace('.', ''));

      const monthEl = document.createElement('div');
      monthEl.className = 'calendar-month';

      // Header
      const header = document.createElement('div');
      header.className = 'calendar-month__header';
      header.textContent = monthNames[month] + ' ' + year;
      monthEl.appendChild(header);

      // Weekdays
      const weekdaysEl = document.createElement('div');
      weekdaysEl.className = 'calendar-month__weekdays';
      weekdays.forEach(day => {
        const dayEl = document.createElement('div');
        dayEl.className = 'calendar-month__weekday';
        dayEl.textContent = day;
        weekdaysEl.appendChild(dayEl);
      });
      monthEl.appendChild(weekdaysEl);

      // Days grid
      const daysEl = document.createElement('div');
      daysEl.className = 'calendar-month__days';

      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // Empty cells
      for (let i = 0; i < firstDay; i++) {
        const emptyEl = document.createElement('div');
        emptyEl.className = 'calendar-day calendar-day--empty';
        daysEl.appendChild(emptyEl);
      }

      // Day cells
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dateStr = DateUtils.toISO(date);
        const dayEl = this.createDayCell(date, dateStr, opts);
        daysEl.appendChild(dayEl);
      }

      // Fill remaining
      const totalCells = firstDay + daysInMonth;
      const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
      for (let i = 0; i < remaining; i++) {
        const emptyEl = document.createElement('div');
        emptyEl.className = 'calendar-day calendar-day--empty';
        daysEl.appendChild(emptyEl);
      }

      monthEl.appendChild(daysEl);
      return monthEl;
    },

    createDayCell(date, dateStr, opts) {
      const dayEl = document.createElement('div');
      dayEl.className = 'calendar-day';
      dayEl.dataset.date = dateStr;

      const dayNum = date.getDate();
      const isToday = DateUtils.isSameDay(date, opts.today);
      const isPast = date < opts.today && !isToday;
      // Use minDate for lead time (date must be >= minDate)
      const isBeforeMinDate = opts.minDate && date < opts.minDate;
      // Use disabledSet from DDMAvailability computation (blackout/vendor closure dates)
      const isInBlackout = opts.disabledSet && opts.disabledSet.has(dateStr);
      // Check if date falls on a weekly closed day (e.g., Sunday for Fresh)
      const closedDays = opts.weeklyClosedDays || [];
      const isWeeklyClosed = closedDays.length > 0 && closedDays.indexOf(date.getDay()) !== -1;
      // [DODOFRESH] Whitelist check: if enabledSet exists, date MUST be in it
      const enabledSet = opts.enabledSet;
      const isNotInWhitelist = enabledSet && !enabledSet.has(dateStr);
      // Disabled if: before minDate, in blackout, NOT in whitelist (when whitelist exists)
      // When whitelist exists, it's more restrictive - weekly closed days are implicit in whitelist
      const isDisabled = isBeforeMinDate || isInBlackout || isNotInWhitelist || (!enabledSet && isWeeklyClosed);
      // Surge detection using surge ranges from metaobjects
      const isSurge = this.isInSurgeRange(dateStr, opts.surgeRanges);

      // Number
      const numEl = document.createElement('span');
      numEl.className = 'calendar-day__number';
      numEl.textContent = dayNum;
      dayEl.appendChild(numEl);

      // Label
      const labelEl = document.createElement('span');
      labelEl.className = 'calendar-day__label';

      const labels = PreCheckoutV2.config.typeLabels;

      // Show the per-date delivery rate (Prime/threshold-aware) instead of a delivery-tier word.
      const rateLabel = function(deliveryType) {
        const price = DeliveryOptionsModule.calculatePrice(deliveryType);
        return price === 0 ? (labels.free || 'FREE') : fmt(price);
      };

      if (isToday) {
        // Today is only selectable if same-day is available AND not blocked by any constraint
        const todaySelectable = opts.isSameDayAvailable && !isBeforeMinDate && !isInBlackout && !isNotInWhitelist;
        if (todaySelectable) {
          dayEl.classList.add('calendar-day--today');
          // Same-day rate
          labelEl.textContent = rateLabel(isSurge ? 'same_day_surge' : 'same_day');
        } else {
          dayEl.classList.add('calendar-day--today', 'calendar-day--disabled');
          // Determine label based on reason (priority: cutoff > whitelist > weeklyClosed > blackout > prep)
          if (!opts.isSameDayAvailable) {
            // Cutoff passed takes priority
            labelEl.textContent = CAL_TXT.cutoff;
          } else if (isNotInWhitelist) {
            // [DODOFRESH] Date not in allowed range (whitelist)
            labelEl.textContent = CAL_TXT.unavail;
          } else if (isWeeklyClosed && !enabledSet) {
            // Weekly closed day (only when no whitelist - whitelist is more restrictive)
            labelEl.textContent = CAL_TXT.notAvail;
          } else if (isInBlackout) {
            labelEl.textContent = CAL_TXT.unavail;
          } else if (isBeforeMinDate && opts.maxLeadHours > 0) {
            labelEl.textContent = opts.maxLeadHours + 'h';
          } else {
            labelEl.textContent = labels.unavailable;
          }
        }
      } else if (isPast) {
        // Past dates: disabled with no label
        dayEl.classList.add('calendar-day--disabled');
        // No label text for past dates
      } else if (isDisabled) {
        dayEl.classList.add('calendar-day--disabled');
        // Determine label based on reason (priority: whitelist > weeklyClosed > blackout > prep)
        if (isNotInWhitelist) {
          // [DODOFRESH] Date not in allowed range (whitelist)
          labelEl.textContent = CAL_TXT.unavail;
        } else if (isWeeklyClosed && !enabledSet) {
          // Weekly closed day - show "NOT AVAILABLE" label (only when no whitelist)
          labelEl.textContent = CAL_TXT.notAvail;
        } else if (isInBlackout) {
          labelEl.textContent = CAL_TXT.unavail;
        } else if (isBeforeMinDate && opts.maxLeadHours > 0) {
          labelEl.textContent = opts.maxLeadHours + 'h';
        }
      } else if (isSurge) {
        // Surge date — gold styling already signals peak; show the (higher) rate
        dayEl.classList.add('calendar-day--surge');
        labelEl.textContent = rateLabel('surge_pricing');
      } else {
        // Standard future date — show the standard rate
        dayEl.classList.add('calendar-day--standard');
        labelEl.textContent = rateLabel('standard');
      }

      dayEl.appendChild(labelEl);
      return dayEl;
    },

    // Map a date to its delivery type (same_day / same_day_surge / surge_pricing / standard)
    deliveryTypeForDate(date) {
      const isToday = this.isSelectedDateToday(date);
      const isSurge = this.isSelectedDateSurge(date);
      if (isToday && isSurge) return 'same_day_surge';
      if (isToday) return 'same_day';
      if (isSurge) return 'surge_pricing';
      return 'standard';
    },

    handleDateSelection(date, dateStr) {
      // Transactional model: tapping a date only updates the PENDING selection (the highlight
      // is handled by the click handler) and the in-modal preview. Nothing is committed to the
      // order until "Confirm Date" (ModalController.saveAndClose -> commitPendingDate).
      this._pendingDate = dateStr;
      this._pendingDateObj = date;

      ModalController.clearError('delivery');
      this.updateSelectedDateDisplay();

      // Enable confirm button
      const saveBtn = document.querySelector('[data-pcv2-modal-save="delivery"]');
      if (saveBtn) saveBtn.disabled = false;
    },

    // Commit the pending date to the order. Called by ModalController.saveAndClose on Confirm.
    commitPendingDate() {
      if (!this._pendingDate) return false;
      const date = this._pendingDateObj || DateUtils.fromDMY(this._pendingDate);
      const deliveryType = this.deliveryTypeForDate(date);
      const before = NoticeManager.selectionKey();
      PreCheckoutV2.state.deliveryDate = this._pendingDate;
      PreCheckoutV2.state.deliveryType = deliveryType;
      PreCheckoutV2.state.deliveryPrice = DeliveryOptionsModule.calculatePrice(deliveryType);
      document.getElementById('pcv2DeliveryDateInput').value = this._pendingDate;
      document.getElementById('pcv2DeliveryTypeInput').value = deliveryType;
      NoticeManager.dropSelectionNotices(before);
      return true;
    },

    // Reset the calendar to reflect the committed order date, discarding any un-confirmed tap
    // from a previous open. Called every time the delivery modal opens.
    syncToCommitted() {
      const calendarEl = document.getElementById('pcv2DeliveryCalendar');
      if (!calendarEl) return;

      const prev = calendarEl.querySelector('.calendar-day--selected');
      if (prev) prev.classList.remove('calendar-day--selected');

      this._pendingDate = PreCheckoutV2.state.deliveryDate || null;
      this._pendingDateObj = this._pendingDate ? DateUtils.fromDMY(this._pendingDate) : null;

      const container = document.querySelector('[data-pcv2-selected-date]');
      const saveBtn = document.querySelector('[data-pcv2-modal-save="delivery"]');

      if (this._pendingDate) {
        const parts = this._pendingDate.split('-'); // DD-MM-YYYY
        const isoDate = parts[2] + '-' + parts[1].padStart(2, '0') + '-' + parts[0].padStart(2, '0');
        const dayEl = calendarEl.querySelector('[data-date="' + isoDate + '"]');
        if (dayEl) dayEl.classList.add('calendar-day--selected');
        this.updateSelectedDateDisplay();
        if (saveBtn) saveBtn.disabled = false;
      } else {
        if (container) container.style.display = 'none';
        if (saveBtn) saveBtn.disabled = true;
      }
    },

    updateSelectedDateDisplay() {
      const container = document.querySelector('[data-pcv2-selected-date]');
      const dateText = document.querySelector('[data-pcv2-selected-date-text]');
      const priceOriginal = document.querySelector('[data-pcv2-selected-price-original]');
      const priceText = document.querySelector('[data-pcv2-selected-price-text]');

      if (!container || !this._pendingDate) return;

      // Preview the PENDING date (not yet committed to the order)
      const date = this._pendingDateObj || DateUtils.fromDMY(this._pendingDate);
      const deliveryType = this.deliveryTypeForDate(date);
      const currentPrice = DeliveryOptionsModule.calculatePrice(deliveryType);
      const basePrice = DeliveryOptionsModule.getBasePrice(deliveryType);

      container.style.display = 'flex';
      if (dateText) {
        const parsedDate = DateUtils.fromDMY(this._pendingDate);
        dateText.textContent = parsedDate ? DateUtils.toDisplayWithWeekday(parsedDate) : this._pendingDate;
      }

      if (priceText) {
        // Show FREE or formatted price
        if (currentPrice === 0) {
          priceText.textContent = PreCheckoutV2.config.typeLabels.free;
          priceText.classList.add('pcv2-selected-date__amount--highlight');
        } else {
          priceText.textContent = this.formatMoney(currentPrice);
          // Add highlight if discounted
          if (currentPrice < basePrice) {
            priceText.classList.add('pcv2-selected-date__amount--highlight');
          } else {
            priceText.classList.remove('pcv2-selected-date__amount--highlight');
          }
        }

        // Show original price strikethrough if discounted
        if (priceOriginal && currentPrice < basePrice) {
          priceOriginal.textContent = this.formatMoney(basePrice);
          priceOriginal.style.display = 'inline';
        } else if (priceOriginal) {
          priceOriginal.style.display = 'none';
        }
      }
    },

    // Utility functions
    /**
     * [RELEASE B] Is same-day delivery available right now?
     *
     * Two independent gates:
     *  1. Policy - sameDayConfig.enabled is false when ANY cart item sets
     *     fulfillment.same_day_enabled = false, or any vertical present in the cart
     *     has same_day_enabled_default = false. Computed since DB-027-P0 but never
     *     consumed until now (spec I-02/I-08).
     *  2. Clock - the vertical's same-day cutoff (config already overridden from
     *     sameDayConfig.cutoff by applyPolicyCutoff).
     *
     * Single choke point: all three call sites inherit both gates.
     * Deliberately re-reads the payload rather than caching - the drawer re-renders
     * it after cart mutations, so a cached flag would go stale mid-session.
     */
    checkSameDayAvailable() {
      const DDM = window.DDMAvailability;
      if (DDM && typeof DDM.isSameDayAvailable === 'function') {
        const payload = DDM.parseJSONScript('pcv2-availability-config');
        if (payload) {
          return DDM.isSameDayAvailable(payload, new Date(), {
            tzOffsetHours: PreCheckoutV2.config.tzOffsetHours
          });
        }
      }
      // Fallback only when DDMAvailability is unavailable. config.cutOffHour was
      // seeded from the payload by applyPolicyCutoff() at init.
      const now = new Date();
      const { tzOffsetHours, cutOffHour, cutOffMinute } = PreCheckoutV2.config;
      const totalOffsetMinutes = now.getTimezoneOffset() + (tzOffsetHours * 60);
      const gmt4Now = new Date(now.getTime() + (totalOffsetMinutes * 60 * 1000));
      // Compare minutes numerically - avoids setHours() date-boundary bug when
      // timezone shift causes gmt4Now to land on a different calendar day
      const currentMinutes = gmt4Now.getHours() * 60 + gmt4Now.getMinutes();
      const cutoffMinutes = cutOffHour * 60 + cutOffMinute;
      return currentMinutes < cutoffMinutes;
    },

    isSelectedDateToday(date) {
      const today = DateUtils.getGMT4Today();
      return DateUtils.isSameDay(date, today);
    },

    isSelectedDateSurge(date) {
      // Use cached surge ranges from metaobjects
      if (!this._surgeRanges || !this._surgeRanges.length) {
        // Fallback: try to fetch if not cached
        this._surgeRanges = this.getSurgeRanges();
      }
      const dateStr = DateUtils.toISO(date);
      return this.isInSurgeRange(dateStr, this._surgeRanges);
    },

    formatMoney(cents) {
      const amount = cents / 100;
      return PreCheckoutV2.config.currencySymbol + ' ' + amount.toLocaleString('en-MU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
  };

  // ==========================================================================
  // Shared Utilities
  // ==========================================================================
  // Alias for shorter formatMoney calls across modules
  const fmt = (cents) => CalendarModule.formatMoney(cents);

  /**
   * Shared paste handler for input fields with maxlength
   * Validates and enforces maxlength, updates state and UI
   * @param {HTMLElement} input - Input/textarea element
   * @param {string} countSelector - Selector for char count display
   * @param {Function} updateStateFn - Callback to update state after paste
   */
  function handlePasteValidation(input, countSelector, updateStateFn) {
    const maxLength = parseInt(input.getAttribute('maxlength'), 10);
    if (!maxLength) return;

    setTimeout(() => {
      const wasTruncated = input.value.length > maxLength;
      if (wasTruncated) {
        input.value = input.value.substring(0, maxLength);
      }

      // Call state update callback
      if (updateStateFn) updateStateFn(input.value);

      // Update char count
      const countEl = document.querySelector(countSelector);
      if (countEl) countEl.textContent = input.value.length;

      // Flash counter red if truncated
      if (wasTruncated && countEl) {
        countEl.style.color = 'var(--color-error, #c00)';
        setTimeout(() => { countEl.style.color = ''; }, 1000);
      }
    }, 0);
  }

  // ==========================================================================
  // Delivery Options Module (V3 Radio Cards)
  // ==========================================================================
  const DeliveryOptionsModule = {
    _sameDayAvailable: false,
    _tomorrowAvailable: true,
    _surgeRanges: null,
    _disabledSet: null,
    _initialized: false,
    _cachedCartTotal: 0,

    // Listeners only. The selection is decided by StateManager.start().
    init() {
      const optionsContainer = document.querySelector('[data-pcv2-delivery-options]');
      if (!optionsContainer) return;
      if (this._initialized) return;

      console.log('[DeliveryOptionsModule] Initializing...');
      this._initialized = true;

      this.bindOptionHandlers();
      this.bindCartEvents();
      // Prices and totals use the cart total Liquid rendered. It cannot go stale: after
      // any cart change the page is rendered again (CartSync).
      const container = document.querySelector('.pcv2-container');
      this._cachedCartTotal = parseInt(container && container.dataset.cartTotal, 10) || 0;
      this.localiseQuickDates();
    },

    // "Today, Sep 30" / "Tomorrow, Oct 1" come from Liquid with English month names and
    // the server's date. Redraw the date part in the page language from the shop clock,
    // so FR reads "Demain, 1 oct." next to a SCHEDULE card that says "ven. 2 oct. 2026".
    localiseQuickDates() {
      const format = new Intl.DateTimeFormat(_PC_LANG, { month: 'short', day: 'numeric' });
      const today = DateUtils.getGMT4Today();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      [['[data-pcv2-fast-subtitle]', today], ['[data-pcv2-tomorrow-subtitle]', tomorrow]].forEach(([selector, date]) => {
        const el = document.querySelector(selector);
        if (el) el.textContent = el.textContent.split(',')[0] + ', ' + format.format(date);
      });
    },

    // With no date to restore, the default becomes a real selection (state, hidden
    // inputs, session) - or no selection at all when the cart has no deliverable date.
    selectDefault() {
      const date = this.firstValidDate();
      if (date) {
        this.applyDate(date);
      } else {
        this.clearSelection();
      }
    },

    // The default date: the first one the shared validator accepts, so an automatic
    // selection is held to the same rule as a stored or a picked date. In order:
    // NEXT-DAY (what the markup preselects), EXPRESS, then the calendar's dates - the
    // allowed-dates whitelist when the cart has one, else the 90 days from the earliest
    // date - preferring a standard-rate date to a surge one.
    // @returns {string|null} DD-MM-YYYY, or null when no date in range suits the cart
    firstValidDate() {
      const disabled = this._disabledSet || new Set();
      const valid = (date) => !disabled.has(DateUtils.toISO(date)) && ValidationModule.checkDate(DateUtils.toDMY(date)).valid;
      const today = DateUtils.getGMT4Today();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (this._tomorrowAvailable && valid(tomorrow)) return DateUtils.toDMY(tomorrow);
      if (this._sameDayAvailable && valid(today)) return DateUtils.toDMY(today);

      let dates;
      if (this._enabledDates) {
        dates = this._enabledDates.slice().sort().map((iso) => DateUtils.fromISO(iso));
      } else {
        const start = this._minDate || CalendarModule.getMinDate();
        dates = Array.from({ length: 90 }, (_, days) => {
          const date = new Date(start);
          date.setDate(date.getDate() + days);
          return date;
        });
      }
      let firstSurge = null; // used when every deliverable date in range is a surge date
      for (const date of dates) {
        if (!date || !valid(date)) continue;
        if (!this.isDateInSurge(date)) return DateUtils.toDMY(date);
        firstSurge = firstSurge || date;
      }
      return firstSurge ? DateUtils.toDMY(firstSurge) : null;
    },

    // Make this date (DD-MM-YYYY) the selection: state, hidden inputs and the matching
    // option card (EXPRESS for today, NEXT-DAY for tomorrow, else SCHEDULE with the date)
    applyDate(dmy) {
      const deliveryType = this.typeForDate(DateUtils.fromDMY(dmy));
      const before = NoticeManager.selectionKey();
      PreCheckoutV2.state.deliveryDate = dmy;
      PreCheckoutV2.state.deliveryType = deliveryType;
      PreCheckoutV2.state.deliveryPrice = this.calculatePrice(deliveryType);
      NoticeManager.dropSelectionNotices(before); // callers that have something to say say it after
      const dateInput = document.getElementById('pcv2DeliveryDateInput');
      const typeInput = document.getElementById('pcv2DeliveryTypeInput');
      if (dateInput) dateInput.value = dmy;
      if (typeInput) typeInput.value = deliveryType;
      this.handleCalendarSelection();
    },

    // Prices everywhere for the current cart total. Never changes the selection.
    reprice() {
      this.renderPricing();
      if (PreCheckoutV2.state.deliveryDate) {
        PreCheckoutV2.state.deliveryPrice = this.calculatePrice(PreCheckoutV2.state.deliveryType);
        const custom = document.querySelector('[data-pcv2-option="custom"]');
        if (custom && custom.classList.contains('pcv2-delivery-option--selected')) this.updateCustomPricing();
      }
      SidebarUpdater.update();
      StateManager.save();
    },

    // The cart drawer is on this page too (header). A change made there makes the page
    // stale: every such event reloads it at once (which also cancels a Continue under way).
    // No reading or comparing first: in this theme both events are fired only after a
    // shopper's cart change (drawer quantity, drawer tiles, Prime checkbox, product forms),
    // never on load or on a timer, so they cannot start a reload loop.
    bindCartEvents() {
      document.addEventListener('cart:updated', () => CartSync.reload({ changed: true }));
      document.addEventListener('ajaxProduct:added', () => CartSync.reload({ changed: true }));
    },

    // Option states only. The selection is the caller's: StateManager.start() at
    // start-up, revalidateSelectedDate() before Continue posts.
    computeAvailability() {
      // Get disabled dates and surge ranges from DDMAvailability
      this._disabledSet = CalendarModule.computeDisabledDates();
      this._enabledDates = CalendarModule._enabledDates;  // [DODOFRESH] Whitelist from allowed_dates_range
      this._surgeRanges = CalendarModule.getSurgeRanges();
      this._weeklyClosedDays = CalendarModule.getWeeklyClosedDays();

      // Get lead time and config - try DDMAvailability first, fallback to direct JSON parse
      var leadDays = CalendarModule.getLeadTimeDays();
      var maxLeadHours = 0;
      var hasAllowedDatesRange = false;

      // Read config for preorder notice text
      try {
        var configEl = document.getElementById('pcv2-availability-config');
        if (configEl) {
          var payload = JSON.parse(configEl.textContent);
          if (payload) {
            maxLeadHours = (payload.sameDayConfig && payload.sameDayConfig.maxLeadHours) || 0;
            hasAllowedDatesRange = payload.hasAllowedDatesRange || false;
            // Fallback lead days if DDMAvailability not loaded
            if (leadDays === 0 && !window.DDMAvailability && maxLeadHours > 0) {
              leadDays = Math.ceil(maxLeadHours / 24);
              console.log('[DeliveryOptionsModule] Fallback lead days from JSON:', leadDays);
            }
          }
        }
      } catch (e) {
        console.warn('[DeliveryOptionsModule] Failed to parse config JSON:', e);
      }

      // Store for notice text generation
      this._maxLeadHours = maxLeadHours;
      this._hasAllowedDatesRange = hasAllowedDatesRange;

      // Get minimum selectable date (includes lead time from prep_time_hours)
      // If DDMAvailability not loaded, calculate minDate manually
      var minDate;
      if (window.DDMAvailability) {
        minDate = CalendarModule.getMinDate();
      } else {
        // Manual calculation: today + (cutoff passed ? 1 : 0) + leadDays
        minDate = DateUtils.getGMT4Today();
        if (!CalendarModule.checkSameDayAvailable()) {
          minDate.setDate(minDate.getDate() + 1);
        }
        if (leadDays > 0) {
          minDate.setDate(minDate.getDate() + leadDays);
        }
      }

      // Cache minDate for use in firstValidDate
      this._minDate = minDate;

      // Check same-day availability (before cutoff, not disabled, and meets lead time)
      const today = DateUtils.getGMT4Today();
      const todayISO = DateUtils.toISO(today);
      const isCutoffPassed = !CalendarModule.checkSameDayAvailable();
      const isTodayDisabled = this._disabledSet && this._disabledSet.has(todayISO);
      // [DODOFRESH] Check whitelist: if exists, today must be in it
      const isTodayNotInWhitelist = this._enabledDates && this._enabledDates.indexOf(todayISO) === -1;
      const isTodayWeeklyClosed = !this._enabledDates && this._weeklyClosedDays.length > 0 && this._weeklyClosedDays.indexOf(today.getDay()) !== -1;
      // Compare dates only (ignore time) - today is before minDate if minDate is a future day
      const isTodayBeforeMinDate = todayISO < DateUtils.toISO(minDate);
      this._sameDayAvailable = !isCutoffPassed && !isTodayDisabled && !isTodayNotInWhitelist && !isTodayBeforeMinDate;

      // Store reason flags for notice text (mutually exclusive, priority order)
      this._sameDayBlockedByCutoff = isCutoffPassed;
      this._sameDayBlockedByWeeklyClosed = !isCutoffPassed && isTodayWeeklyClosed;
      this._sameDayBlockedByBlackout = !isCutoffPassed && !isTodayWeeklyClosed && (isTodayDisabled || isTodayNotInWhitelist || hasAllowedDatesRange);
      this._sameDayBlockedByPrep = !isCutoffPassed && !this._sameDayBlockedByBlackout && !isTodayWeeklyClosed && isTodayBeforeMinDate && maxLeadHours > 0;

      // Check tomorrow availability (not disabled and meets lead time)
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowISO = DateUtils.toISO(tomorrow);
      const isTomorrowDisabled = this._disabledSet && this._disabledSet.has(tomorrowISO);
      // [DODOFRESH] Check whitelist: if exists, tomorrow must be in it
      const isTomorrowNotInWhitelist = this._enabledDates && this._enabledDates.indexOf(tomorrowISO) === -1;
      const isTomorrowWeeklyClosed = !this._enabledDates && this._weeklyClosedDays.length > 0 && this._weeklyClosedDays.indexOf(tomorrow.getDay()) !== -1;
      // Compare dates only (ignore time)
      const isTomorrowBeforeMinDate = tomorrowISO < DateUtils.toISO(minDate);
      this._tomorrowAvailable = !isTomorrowDisabled && !isTomorrowNotInWhitelist && !isTomorrowBeforeMinDate;

      // Store reason flags for notice text (mutually exclusive, priority order)
      this._tomorrowBlockedByWeeklyClosed = isTomorrowWeeklyClosed;
      this._tomorrowBlockedByBlackout = !isTomorrowWeeklyClosed && (isTomorrowDisabled || isTomorrowNotInWhitelist || hasAllowedDatesRange);
      this._tomorrowBlockedByPrep = !this._tomorrowBlockedByBlackout && !isTomorrowWeeklyClosed && isTomorrowBeforeMinDate && maxLeadHours > 0;

      console.log('[DeliveryOptionsModule] Lead days:', leadDays, 'Min date:', DateUtils.toISO(minDate), 'Today:', todayISO, 'Tomorrow:', tomorrowISO);
      console.log('[DeliveryOptionsModule] Same-day available:', this._sameDayAvailable, '(beforeMin:', isTodayBeforeMinDate, ') Tomorrow available:', this._tomorrowAvailable, '(beforeMin:', isTomorrowBeforeMinDate, ')');

      this.updateOptionStates();
    },

    updateOptionStates() {
      const fastOption = document.querySelector('[data-pcv2-option="fast"]');
      const tomorrowOption = document.querySelector('[data-pcv2-option="tomorrow"]');
      const fastNotice = document.querySelector('[data-pcv2-fast-notice]');
      const fastNoticeText = document.querySelector('[data-pcv2-fast-notice-text]');
      const tomorrowNotice = document.querySelector('[data-pcv2-tomorrow-notice]');
      const tomorrowNoticeText = document.querySelector('[data-pcv2-tomorrow-notice-text]');
      // [DODOFRESH] Locale detection for translated notice text
      const _lang = document.documentElement.lang || 'en';
      const _isFr = _lang.indexOf('fr') === 0;
      const _naPrefix = _isFr
        ? 'Livraison N/A pour certains articles le : '
        : 'For some items in cart delivery N/A on: ';
      const _unavailPrefix = _isFr
        ? 'Livraison indisponible pour certains articles le '
        : 'For some items in cart delivery unavailable on ';

      if (fastOption) {
        if (!this._sameDayAvailable) {
          fastOption.classList.add('pcv2-delivery-option--disabled');
          fastOption.querySelector('input').disabled = true;
          fastOption.setAttribute('data-available', 'false');
          // Update notice text based on reason (priority: cutoff > weeklyClosed > blackout > prep)
          if (fastNoticeText) {
            if (this._sameDayBlockedByCutoff) {
              // Reset to default "Same-day delivery cutoff has passed"
              fastNoticeText.textContent = fastNoticeText.dataset.defaultText || 'Same-day delivery cutoff has passed';
            } else if (this._sameDayBlockedByWeeklyClosed) {
              var closedText = window.DDMAvailability && window.DDMAvailability.formatWeeklyClosedDays
                ? window.DDMAvailability.formatWeeklyClosedDays(this._weeklyClosedDays) : (_isFr ? 'aujourd\'hui' : 'today');
              fastNoticeText.textContent = _naPrefix + closedText;
            } else if (this._sameDayBlockedByBlackout) {
              var todayFormatted = window.DDMAvailability && window.DDMAvailability.formatHuman
                ? window.DDMAvailability.formatHuman(DateUtils.toISO(DateUtils.getGMT4Today()), _lang) : (_isFr ? 'aujourd\'hui' : 'today');
              fastNoticeText.textContent = _naPrefix + todayFormatted;
            } else if (this._sameDayBlockedByPrep) {
              fastNoticeText.textContent = _isFr
                ? 'Certains articles nécessitent : ' + this._maxLeadHours + ' heures de préparation'
                : 'Some items in cart require: ' + this._maxLeadHours + ' hours preorder';
            }
          }
          // Show inline notice under Fast option
          if (fastNotice) fastNotice.style.display = 'flex';
        } else {
          fastOption.classList.remove('pcv2-delivery-option--disabled');
          fastOption.querySelector('input').disabled = false;
          fastOption.setAttribute('data-available', 'true');
          if (fastNotice) fastNotice.style.display = 'none';
        }
      }

      if (tomorrowOption) {
        if (!this._tomorrowAvailable) {
          tomorrowOption.classList.add('pcv2-delivery-option--disabled');
          tomorrowOption.querySelector('input').disabled = true;
          tomorrowOption.setAttribute('data-available', 'false');
          // Update notice text based on reason (priority: weeklyClosed > blackout > prep)
          if (tomorrowNoticeText) {
            if (this._tomorrowBlockedByWeeklyClosed) {
              var closedTextTmw = window.DDMAvailability && window.DDMAvailability.formatWeeklyClosedDays
                ? window.DDMAvailability.formatWeeklyClosedDays(this._weeklyClosedDays) : (_isFr ? 'demain' : 'tomorrow');
              tomorrowNoticeText.textContent = _unavailPrefix + closedTextTmw;
            } else if (this._tomorrowBlockedByBlackout) {
              var tmw = new Date(DateUtils.getGMT4Today());
              tmw.setDate(tmw.getDate() + 1);
              var tmwFormatted = window.DDMAvailability && window.DDMAvailability.formatHuman
                ? window.DDMAvailability.formatHuman(DateUtils.toISO(tmw), _lang) : (_isFr ? 'demain' : 'tomorrow');
              tomorrowNoticeText.textContent = _unavailPrefix + tmwFormatted;
            } else if (this._tomorrowBlockedByPrep) {
              tomorrowNoticeText.textContent = _isFr
                ? 'Certains articles nécessitent : ' + this._maxLeadHours + ' heures de préparation'
                : 'Some items in cart require: ' + this._maxLeadHours + ' hours preorder';
            } else {
              // Reset to default for other cases
              tomorrowNoticeText.textContent = tomorrowNoticeText.dataset.defaultText || 'Tomorrow is unavailable';
            }
          }
          // Show inline notice under Tomorrow option
          if (tomorrowNotice) tomorrowNotice.style.display = 'flex';
        } else {
          tomorrowOption.classList.remove('pcv2-delivery-option--disabled');
          tomorrowOption.querySelector('input').disabled = false;
          tomorrowOption.setAttribute('data-available', 'true');
          if (tomorrowNotice) tomorrowNotice.style.display = 'none';
        }
      }

      // [UPSELL S5] Explain options that only an upsell extra is blocking
      ExtrasDateCheck.run();
    },

    bindOptionHandlers() {
      const options = document.querySelectorAll('[data-pcv2-option]');
      options.forEach(option => {
        option.addEventListener('click', (e) => {
          // Disabled - or the page is locked (a cart request or a reload is under way)
          if (option.classList.contains('pcv2-delivery-option--disabled') || CartSync.busy) {
            e.preventDefault();
            return;
          }
          CartSync._staleReloads = 0; // the shopper acts on the page: count from zero again

          const optionType = option.dataset.pcv2Option;
          this.handleOptionSelect(optionType);
        });
      });
    },

    handleOptionSelect(optionType) {
      console.log('[DeliveryOptionsModule] Option selected:', optionType);

      if (optionType === 'custom') {
        // Open calendar modal for custom date selection
        // Don't change selection yet - handleCalendarSelection will do it if date is saved
        ModalController.open('delivery');
        return;
      }

      // Remove previous selection
      document.querySelectorAll('[data-pcv2-option]').forEach(opt => {
        opt.classList.remove('pcv2-delivery-option--selected');
      });

      const selectedOption = document.querySelector(`[data-pcv2-option="${optionType}"]`);
      if (selectedOption) {
        selectedOption.classList.add('pcv2-delivery-option--selected');
        selectedOption.querySelector('input').checked = true;
      }

      // Calculate date and pricing
      const today = DateUtils.getGMT4Today();
      let selectedDate, deliveryType, dateStr;

      if (optionType === 'fast') {
        selectedDate = today;
        const isSurge = this.isDateInSurge(selectedDate);
        deliveryType = isSurge ? 'same_day_surge' : 'same_day';
        dateStr = DateUtils.toDMY(selectedDate);
      } else if (optionType === 'tomorrow') {
        selectedDate = new Date(today);
        selectedDate.setDate(selectedDate.getDate() + 1);
        const isSurge = this.isDateInSurge(selectedDate);
        deliveryType = isSurge ? 'surge_pricing' : 'standard';
        dateStr = DateUtils.toDMY(selectedDate);
      }

      // Calculate price based on full rate matrix
      const deliveryPrice = this.calculatePrice(deliveryType);

      // Update state
      const before = NoticeManager.selectionKey();
      PreCheckoutV2.state.deliveryDate = dateStr;
      PreCheckoutV2.state.deliveryType = deliveryType;
      PreCheckoutV2.state.deliveryPrice = deliveryPrice;
      NoticeManager.dropSelectionNotices(before);

      // Update hidden inputs
      document.getElementById('pcv2DeliveryDateInput').value = dateStr;
      document.getElementById('pcv2DeliveryTypeInput').value = deliveryType;

      // SCHEDULE is no longer the selection: its card goes back to the prompt and the
      // "no date picked" price, instead of still naming the date it had
      const customSubtitle = document.querySelector('[data-pcv2-custom-subtitle]');
      if (customSubtitle && customSubtitle.dataset.defaultText) customSubtitle.textContent = customSubtitle.dataset.defaultText;
      this.renderPricing();

      // Update sidebar and save state
      SidebarUpdater.update();
      StateManager.save();
    },

    calculatePrice(deliveryType) {
      const config = PreCheckoutV2.config;
      const cartTotal = this._cachedCartTotal;
      const threshold = config.freeDeliveryThreshold || 199900;
      const aboveThreshold = cartTotal >= threshold;

      // Check legacy Prime + gifts scenario
      const isLegacyPrime = document.querySelector('[data-is-legacy-prime="true"]') !== null;
      const hasGifts = document.querySelector('[data-cart-has-gifts="true"]') !== null;
      const legacyFreeDelivery = isLegacyPrime && hasGifts;

      const isSurge = deliveryType.includes('surge');
      const isSameDay = deliveryType.includes('same_day');

      // Rate matrix logic
      if (legacyFreeDelivery) {
        if (isSameDay) return isSurge ? config.legacyPrimeSurgeSdRate : config.legacyPrimeSameDayRate;
        return isSurge ? config.legacyPrimeSurgeStdRate : config.legacyPrimeStandardRate;
      }

      if (!PreCheckoutV2.isPrimeMember) {
        if (isSameDay) return isSurge ? config.surgeSameDayRate : config.sameDayRate;
        return isSurge ? config.surgeStandardRate : config.standardRate;
      }

      // Prime member
      if (aboveThreshold) {
        if (isSameDay) return isSurge ? config.primeSurgeAboveSdRate : config.primeSameDayAboveRate;
        return isSurge ? config.primeSurgeAboveStdRate : config.primeStandardAboveRate;
      } else {
        if (isSameDay) return isSurge ? config.primeSurgeBelowSdRate : config.primeSameDayBelowRate;
        return isSurge ? config.primeSurgeBelowStdRate : config.primeStandardBelowRate;
      }
    },

    // The standard delivery rate for this cart (Prime rules included), for a Continue
    // without a date. The plain standard rate when even that cannot be worked out: the
    // shipping method itself is chosen by delivery_type + prime_status (Craftly).
    standardRate() {
      try {
        return this.calculatePrice('standard');
      } catch (error) {
        console.error('[DeliveryOptionsModule] Standard rate:', error);
        return PreCheckoutV2.config.standardRate;
      }
    },

    // Returns non-Prime base price for comparison (to show savings)
    getBasePrice(deliveryType) {
      const config = PreCheckoutV2.config;
      const isSurge = deliveryType.includes('surge');
      const isSameDay = deliveryType.includes('same_day');
      if (isSameDay) {
        return isSurge ? config.surgeSameDayRate : config.sameDayRate;
      }
      return isSurge ? config.surgeStandardRate : config.standardRate;
    },

    isDateInSurge(date) {
      // Fallback: load surge ranges if not cached
      if (!this._surgeRanges || !this._surgeRanges.length) {
        this._surgeRanges = CalendarModule.getSurgeRanges();
      }
      if (!this._surgeRanges || !this._surgeRanges.length) return false;
      const dateStr = DateUtils.toISO(date);
      return this._surgeRanges.some(r => dateStr >= r.start && dateStr <= r.end);
    },

    // What the clock alone says about a date (DD-MM-YYYY), without the availability data:
    // a real day, not past, and if it is today, before the same-day cutoff
    clockAllows(dmy) {
      const date = DateUtils.readDMY(dmy);
      const today = DateUtils.getGMT4Today();
      if (!date || date < today) return false;
      return !DateUtils.isSameDay(date, today) || CalendarModule.checkSameDayAvailable();
    },

    // same_day / same_day_surge / surge_pricing / standard, from the ranges computeAvailability just read
    typeForDate(date) {
      const isToday = DateUtils.isSameDay(date, DateUtils.getGMT4Today());
      const isSurge = this.isDateInSurge(date);
      if (isToday) return isSurge ? 'same_day_surge' : 'same_day';
      return isSurge ? 'surge_pricing' : 'standard';
    },

    /**
     * Re-check the selected date with the shared validator where Continue posts it:
     * the clock may have passed the cutoff, or midnight, since the page was rendered.
     *   invalid -> selection cleared (Continue then asks for a date) + notice
     *   valid   -> option card, delivery type and price redrawn for the day it is now
     * @returns {boolean} false when the selection was cleared
     */
    revalidateSelectedDate() {
      const stored = PreCheckoutV2.state.deliveryDate;
      if (!stored) return true;
      const check = ValidationModule.checkDate(stored);
      if (!check.valid) {
        this.clearSelection();
        ExtrasDateCheck.clearLost(); // an alert naming a date as "now applied" would be stale
        NoticeManager.show('invalid', {
          date: stored,
          reason: check.reason,
          earliestDate: check.earliestDate,
          outcome: 'cleared'
        });
        return false;
      }
      this.applyDate(stored);
      return true;
    },

    // No delivery date: state, hidden inputs and the option cards all say so
    clearSelection() {
      const before = NoticeManager.selectionKey();
      PreCheckoutV2.state.deliveryDate = null;
      PreCheckoutV2.state.deliveryType = 'standard';
      PreCheckoutV2.state.deliveryPrice = 0; // the summary shows no delivery amount without a date
      NoticeManager.dropSelectionNotices(before);
      const dateInput = document.getElementById('pcv2DeliveryDateInput');
      const typeInput = document.getElementById('pcv2DeliveryTypeInput');
      if (dateInput) dateInput.value = '';
      if (typeInput) typeInput.value = '';
      document.querySelectorAll('[data-pcv2-option]').forEach((opt) => {
        opt.classList.remove('pcv2-delivery-option--selected');
        const radio = opt.querySelector('input');
        if (radio) radio.checked = false;
      });
      const subtitle = document.querySelector('[data-pcv2-custom-subtitle]');
      if (subtitle && subtitle.dataset.defaultText) subtitle.textContent = subtitle.dataset.defaultText;
      this.renderPricing(); // Schedule price back to its "no date picked" preview
    },

    renderPricing() {
      // Update price displays for each option based on current state
      const config = PreCheckoutV2.config;
      const today = DateUtils.getGMT4Today();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todaySurge = this.isDateInSurge(today);
      const tomorrowSurge = this.isDateInSurge(tomorrow);

      // Calculate prices for display
      const fastType = todaySurge ? 'same_day_surge' : 'same_day';
      const tomorrowType = tomorrowSurge ? 'surge_pricing' : 'standard';

      const fastPrice = this.calculatePrice(fastType);
      const tomorrowPrice = this.calculatePrice(tomorrowType);

      // Base prices (what non-Prime pays) for showing strikethrough
      const fastBase = todaySurge ? config.surgeSameDayRate : config.sameDayRate;
      const tomorrowBase = tomorrowSurge ? config.surgeStandardRate : config.standardRate;

      // Update Fast option
      const fastPriceEl = document.querySelector('[data-pcv2-fast-price]');
      const fastOriginalEl = document.querySelector('[data-pcv2-fast-original]');
      if (fastPriceEl) {
        if (fastPrice === 0) {
          fastPriceEl.textContent = PreCheckoutV2.config.typeLabels.free;
          fastPriceEl.classList.add('pcv2-option__price-final--highlight');
        } else {
          fastPriceEl.textContent = fmt(fastPrice);
          fastPriceEl.classList.remove('pcv2-option__price-final--highlight');
        }
        // Show original if discounted
        if (fastOriginalEl && fastPrice < fastBase) {
          fastOriginalEl.textContent = fmt(fastBase);
          fastOriginalEl.style.display = 'inline';
          fastPriceEl.classList.add('pcv2-option__price-final--highlight');
        } else if (fastOriginalEl) {
          fastOriginalEl.style.display = 'none';
        }
      }

      // Update Tomorrow option
      const tomorrowPriceEl = document.querySelector('[data-pcv2-tomorrow-price]');
      const tomorrowOriginalEl = document.querySelector('[data-pcv2-tomorrow-original]');
      if (tomorrowPriceEl) {
        if (tomorrowPrice === 0) {
          tomorrowPriceEl.textContent = PreCheckoutV2.config.typeLabels.free;
          tomorrowPriceEl.classList.add('pcv2-option__price-final--highlight');
        } else {
          tomorrowPriceEl.textContent = fmt(tomorrowPrice);
          tomorrowPriceEl.classList.remove('pcv2-option__price-final--highlight');
        }
        // Show original if discounted
        if (tomorrowOriginalEl && tomorrowPrice < tomorrowBase) {
          tomorrowOriginalEl.textContent = fmt(tomorrowBase);
          tomorrowOriginalEl.style.display = 'inline';
          tomorrowPriceEl.classList.add('pcv2-option__price-final--highlight');
        } else if (tomorrowOriginalEl) {
          tomorrowOriginalEl.style.display = 'none';
        }
      }

      // Update Custom option - no date picked yet.
      // During surge, tomorrow is a surge date but the customer can still schedule a
      // cheaper non-surge date, so preview the floor rate prefixed with "From" instead
      // of tomorrow's surge price. (Exact price resolves once a date is selected.)
      const customPriceEl = document.querySelector('[data-pcv2-custom-price]');
      const customOriginalEl = document.querySelector('[data-pcv2-custom-original]');
      const customIsSurge = tomorrowSurge;
      const customBase = customIsSurge ? config.standardRate : tomorrowBase;
      const customPrice = customIsSurge ? this.calculatePrice('standard') : tomorrowPrice;

      if (customPriceEl) {
        const isFr = (document.documentElement.lang || 'en').startsWith('fr');
        if (customPrice === 0) {
          customPriceEl.textContent = PreCheckoutV2.config.typeLabels.free;
          customPriceEl.classList.add('pcv2-option__price-final--highlight');
        } else if (customIsSurge) {
          customPriceEl.textContent = (isFr ? 'Dès ' : 'From ') + NoticeManager.priceInText(customPrice, isFr); // never 0 here
          customPriceEl.classList.remove('pcv2-option__price-final--highlight');
        } else {
          customPriceEl.textContent = fmt(customPrice);
          customPriceEl.classList.remove('pcv2-option__price-final--highlight');
        }
        // Strikethrough original only for the normal-period prime discount.
        // Suppressed during the surge "From" preview to avoid double messaging.
        if (customOriginalEl && !customIsSurge && customPrice < customBase) {
          customOriginalEl.textContent = fmt(customBase);
          customOriginalEl.style.display = 'inline';
          customPriceEl.classList.add('pcv2-option__price-final--highlight');
        } else if (customOriginalEl) {
          customOriginalEl.style.display = 'none';
        }
      }
    },

    // Called when calendar modal saves a date (for custom option)
    handleCalendarSelection() {
      if (!PreCheckoutV2.state.deliveryDate) return;

      // Parse saved date (dd-mm-yyyy format)
      const parts = PreCheckoutV2.state.deliveryDate.split('-');
      if (parts.length !== 3) return;

      // Use noon to avoid timezone boundary issues
      const savedDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]), 12, 0, 0);
      const today = DateUtils.getGMT4Today();
      today.setHours(12, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Check fast option (today)
      const fastOption = document.querySelector('[data-pcv2-option="fast"]');
      const isFastDisabled = fastOption?.classList.contains('pcv2-delivery-option--disabled');
      const isToday = DateUtils.isSameDay(savedDate, today);

      // Check tomorrow option
      const tomorrowOption = document.querySelector('[data-pcv2-option="tomorrow"]');
      const isTomorrowDisabled = tomorrowOption?.classList.contains('pcv2-delivery-option--disabled');
      const isTomorrow = DateUtils.isSameDay(savedDate, tomorrow);

      // Determine which option to select
      if (isToday && !isFastDisabled) {
        this.handleOptionSelect('fast');
        return;
      }
      if (isTomorrow && !isTomorrowDisabled) {
        this.handleOptionSelect('tomorrow');
        return;
      }

      // Default: select custom option
      document.querySelectorAll('[data-pcv2-option]').forEach(opt => {
        opt.classList.remove('pcv2-delivery-option--selected');
      });
      const customOption = document.querySelector('[data-pcv2-option="custom"]');
      if (customOption) {
        customOption.classList.add('pcv2-delivery-option--selected');
        customOption.querySelector('input').checked = true;

        // Update subtitle with selected date
        const customSubtitle = document.querySelector('[data-pcv2-custom-subtitle]');
        if (customSubtitle) {
          const parsedDate = DateUtils.fromDMY(PreCheckoutV2.state.deliveryDate);
          customSubtitle.textContent = parsedDate ? DateUtils.toDisplayWithWeekday(parsedDate) : PreCheckoutV2.state.deliveryDate;
        }

        // Update custom option pricing based on selected date
        this.updateCustomPricing();
      }
    },

    // Update custom option pricing based on currently selected date
    updateCustomPricing() {
      const config = PreCheckoutV2.config;
      const dateStr = PreCheckoutV2.state.deliveryDate;
      if (!dateStr) return;

      // Parse dd-mm-yyyy to Date
      const parts = dateStr.split('-');
      if (parts.length !== 3) return;
      const selectedDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]), 12, 0, 0);

      // Check if selected date is in surge period
      const isSurge = this.isDateInSurge(selectedDate);
      const deliveryType = isSurge ? 'surge_pricing' : 'standard';

      // Calculate price and base
      const customPrice = this.calculatePrice(deliveryType);
      const customBase = isSurge ? config.surgeStandardRate : config.standardRate;

      // Update display
      const customPriceEl = document.querySelector('[data-pcv2-custom-price]');
      const customOriginalEl = document.querySelector('[data-pcv2-custom-original]');

      if (customPriceEl) {
        if (customPrice === 0) {
          customPriceEl.textContent = PreCheckoutV2.config.typeLabels.free;
          customPriceEl.classList.add('pcv2-option__price-final--highlight');
        } else {
          customPriceEl.textContent = fmt(customPrice);
          customPriceEl.classList.remove('pcv2-option__price-final--highlight');
        }
        // Show original if discounted
        if (customOriginalEl && customPrice < customBase) {
          customOriginalEl.textContent = fmt(customBase);
          customOriginalEl.style.display = 'inline';
          customPriceEl.classList.add('pcv2-option__price-final--highlight');
        } else if (customOriginalEl) {
          customOriginalEl.style.display = 'none';
        }
      }
    }
  };


  // ==========================================================================
  // Gift Module
  // ==========================================================================
  const GiftModule = {
    templates: {},
    FIELDS: {
      occasion: '[data-pcv2-gift-occasion]', greeting: '[data-pcv2-gift-greeting]',
      message: '[data-pcv2-gift-message]', signature: '[data-pcv2-gift-signature]'
    },
    // The gift message Liquid put in the fields: what the cart has (an earlier Continue)
    rendered: null,

    // The four fields as they are on the page / set them
    read() {
      const gift = {};
      Object.keys(this.FIELDS).forEach((name) => {
        const field = document.querySelector(this.FIELDS[name]);
        gift[name] = field ? field.value : '';
      });
      return gift;
    },

    write(gift) {
      Object.keys(this.FIELDS).forEach((name) => {
        const field = document.querySelector(this.FIELDS[name]);
        if (field) field.value = gift[name] || '';
      });
    },

    // Does the cart have a gift message (as rendered)?
    stored() {
      return !!this.rendered && Object.keys(this.rendered).some((name) => !!this.rendered[name]);
    },

    // Every edit and every erase says so, and it is in the session at once: the intent
    // must survive whatever reloads the page next, also when the text ends up equal to
    // what the form started with.
    touch() {
      PreCheckoutV2.state.giftTouched = true;
      StateManager.save();
    },

    init() {
      // Load templates
      const templatesEl = document.getElementById('pcv2GiftMessageTemplates');
      if (templatesEl) {
        try {
          this.templates = JSON.parse(templatesEl.textContent);
        } catch (e) {
          console.error('[GiftModule] Failed to parse templates:', e);
        }
      }

      const lang = document.documentElement.lang || 'en';
      this.langKey = lang.startsWith('fr') ? 'fr' : 'en';

      // Occasion change
      const occasionSelect = document.querySelector('[data-pcv2-gift-occasion]');
      if (occasionSelect) {
        occasionSelect.addEventListener('change', () => {
          const occasion = occasionSelect.value;
          PreCheckoutV2.state.giftMessage.occasion = occasion;

          // Apply template when occasion selected (always replace)
          if (occasion && this.templates[occasion]) {
            const template = this.templates[occasion][this.langKey] || this.templates[occasion].en || '';
            const messageInput = document.querySelector('[data-pcv2-gift-message]');
            if (messageInput) {
              messageInput.value = template;
              PreCheckoutV2.state.giftMessage.message = template;
              this.updateCharCount(messageInput, '[data-pcv2-message-count]');
            }
          }
          this.updateModalPreview();
          CardUpdater.updateGiftCard();
          this.touch();
        });
      }

      // Greeting input
      const greetingInput = document.querySelector('[data-pcv2-gift-greeting]');
      if (greetingInput) {
        greetingInput.addEventListener('input', () => {
          PreCheckoutV2.state.giftMessage.greeting = greetingInput.value;
          this.updateCharCount(greetingInput, '[data-pcv2-greeting-count]');
          this.updateModalPreview();
          CardUpdater.updateGiftCard();
          this.touch();
        });
        // JS-M1: Paste validation
        greetingInput.addEventListener('paste', () => {
          handlePasteValidation(greetingInput, '[data-pcv2-greeting-count]', (val) => {
            PreCheckoutV2.state.giftMessage.greeting = val;
            this.updateModalPreview();
            CardUpdater.updateGiftCard();
            this.touch();
          });
        });
      }

      // Message input
      const messageInput = document.querySelector('[data-pcv2-gift-message]');
      if (messageInput) {
        messageInput.addEventListener('input', () => {
          PreCheckoutV2.state.giftMessage.message = messageInput.value;
          this.updateCharCount(messageInput, '[data-pcv2-message-count]');
          this.updateModalPreview();
          CardUpdater.updateGiftCard();
          this.touch();
        });
        // JS-M1: Paste validation
        messageInput.addEventListener('paste', () => {
          handlePasteValidation(messageInput, '[data-pcv2-message-count]', (val) => {
            PreCheckoutV2.state.giftMessage.message = val;
            this.updateModalPreview();
            CardUpdater.updateGiftCard();
            this.touch();
          });
        });
      }

      // Signature input
      const signatureInput = document.querySelector('[data-pcv2-gift-signature]');
      if (signatureInput) {
        signatureInput.addEventListener('input', () => {
          PreCheckoutV2.state.giftMessage.signature = signatureInput.value;
          this.updateCharCount(signatureInput, '[data-pcv2-signature-count]');
          this.updateModalPreview();
          CardUpdater.updateGiftCard();
          this.touch();
        });
        // JS-M1: Paste validation
        signatureInput.addEventListener('paste', () => {
          handlePasteValidation(signatureInput, '[data-pcv2-signature-count]', (val) => {
            PreCheckoutV2.state.giftMessage.signature = val;
            this.updateModalPreview();
            CardUpdater.updateGiftCard();
            this.touch();
          });
        });
      }

      // Start from the cart's own gift message: the shopper sees what the order carries
      if (occasionSelect && occasionSelect.dataset.stored) occasionSelect.value = occasionSelect.dataset.stored;
      this.rendered = this.read();
      if (this.stored()) {
        PreCheckoutV2.state.giftMessage = Object.assign({}, this.rendered);
        this.updateCharCounts();
        this.updateModalPreview();
        CardUpdater.updateGiftCard();
      }
    },

    updateCharCount(input, countSelector) {
      const countEl = document.querySelector(countSelector);
      if (countEl) {
        countEl.textContent = input.value.length;
      }
    },

    updateCharCounts() {
      const greetingInput = document.querySelector('[data-pcv2-gift-greeting]');
      const messageInput = document.querySelector('[data-pcv2-gift-message]');
      const signatureInput = document.querySelector('[data-pcv2-gift-signature]');

      if (greetingInput) this.updateCharCount(greetingInput, '[data-pcv2-greeting-count]');
      if (messageInput) this.updateCharCount(messageInput, '[data-pcv2-message-count]');
      if (signatureInput) this.updateCharCount(signatureInput, '[data-pcv2-signature-count]');
    },

    updateModalPreview() {
      const previewContainer = document.querySelector('[data-pcv2-gift-modal-preview]');
      const { greeting, message, signature } = PreCheckoutV2.state.giftMessage;

      if (!previewContainer) return;

      const hasContent = greeting || message || signature;
      previewContainer.style.display = hasContent ? 'block' : 'none';

      if (hasContent) {
        const greetingEl = document.querySelector('[data-pcv2-modal-preview-greeting]');
        const messageEl = document.querySelector('[data-pcv2-modal-preview-message]');
        const signatureEl = document.querySelector('[data-pcv2-modal-preview-signature]');

        if (greetingEl) greetingEl.textContent = greeting;
        if (messageEl) messageEl.textContent = message;
        if (signatureEl) signatureEl.textContent = signature;
      }
    }
  };

  // ==========================================================================
  // Notes Module (Order/Delivery Notes) - Modal pattern
  // ==========================================================================
  const NotesModule = {
    init() {
      const textarea = document.querySelector('[data-pcv2-notes-input]');
      if (!textarea) return;

      // Bind input event
      // The draft is in the session as it is typed (an emptied field too): it must
      // survive whatever reloads the page next
      textarea.addEventListener('input', () => {
        PreCheckoutV2.state.orderNotes = textarea.value;
        this.updateCharCount();
        CardUpdater.updateNotesCard(); // the card always shows what Continue will send
        StateManager.save();
      });

      // JS-M1: Paste validation
      textarea.addEventListener('paste', () => {
        handlePasteValidation(textarea, '[data-pcv2-notes-count]', (val) => {
          PreCheckoutV2.state.orderNotes = val;
          CardUpdater.updateNotesCard();
          StateManager.save();
        });
      });

      // The cart's own note (pre-populated in Liquid) is shown, but it is not a draft
      if (textarea.value.trim()) {
        this.updateCharCount();
        CardUpdater.updateNotesCard();
      }
    },

    // The note as the page shows it: the shopper's draft, else what the cart has
    text() {
      const draft = PreCheckoutV2.state.orderNotes;
      if (typeof draft === 'string') return draft;
      const textarea = document.querySelector('[data-pcv2-notes-input]');
      return textarea ? textarea.value : '';
    },

    updateCharCount() {
      const textarea = document.querySelector('[data-pcv2-notes-input]');
      const counter = document.querySelector('[data-pcv2-notes-count]');
      if (textarea && counter) {
        counter.textContent = textarea.value.length;
      }
    },

    restore() {
      const textarea = document.querySelector('[data-pcv2-notes-input]');
      if (textarea && typeof PreCheckoutV2.state.orderNotes === 'string') {
        textarea.value = PreCheckoutV2.state.orderNotes;
        this.updateCharCount();
        CardUpdater.updateNotesCard();
      }
    }
  };

  // ==========================================================================
  // Card Updater
  // ==========================================================================
  const CardUpdater = {
    updateDeliveryCard() {
      const card = document.querySelector('[data-pcv2-card="delivery"]');
      if (!card) return;

      const status = card.querySelector('[data-pcv2-delivery-status]');
      const actionText = card.querySelector('[data-pcv2-delivery-action-text]');
      const actionBtn = card.querySelector('[data-pcv2-open-modal="delivery"]');
      const preview = card.querySelector('[data-pcv2-delivery-preview]');
      const subtitle = card.querySelector('[data-pcv2-delivery-subtitle]');

      if (PreCheckoutV2.state.deliveryDate) {
        // Show completion
        if (status) status.classList.add('is-complete');
        if (actionText) actionText.textContent = 'Edit';
        if (actionBtn) actionBtn.classList.add('pcv2-card__action--edit');
        if (preview) preview.classList.add('is-visible');
        if (subtitle) subtitle.style.display = 'none';

        // Update preview content
        const dateEl = preview?.querySelector('[data-pcv2-preview-date]');
        const typeEl = preview?.querySelector('[data-pcv2-preview-type]');
        const badgeEl = preview?.querySelector('[data-pcv2-preview-badge]');

        if (dateEl) {
          const parsedDate = DateUtils.fromDMY(PreCheckoutV2.state.deliveryDate);
          dateEl.textContent = parsedDate ? DateUtils.toDisplayWithWeekday(parsedDate) : PreCheckoutV2.state.deliveryDate;
        }

        if (typeEl) typeEl.textContent = PreCheckoutV2.config.typeLabels[PreCheckoutV2.state.deliveryType];

        if (badgeEl) {
          badgeEl.textContent = PreCheckoutV2.state.deliveryPrice === 0
            ? PreCheckoutV2.config.typeLabels.free
            : CalendarModule.formatMoney(PreCheckoutV2.state.deliveryPrice);

          // Badge variant
          badgeEl.className = 'pcv2-delivery-preview__badge';
          if (PreCheckoutV2.state.deliveryType === 'surge_pricing' || PreCheckoutV2.state.deliveryType === 'same_day_surge') {
            badgeEl.classList.add('pcv2-delivery-preview__badge--surge');
          } else if (PreCheckoutV2.state.deliveryType === 'same_day') {
            badgeEl.classList.add('pcv2-delivery-preview__badge--sameday');
          }
        }
      }
    },

    updateGiftCard() {
      const card = document.querySelector('[data-pcv2-card="gift"]');
      if (!card) return;

      const { greeting, message, signature } = PreCheckoutV2.state.giftMessage;
      const hasContent = greeting || message || signature;

      const actionText = card.querySelector('[data-pcv2-gift-action-text]');
      const preview = card.querySelector('[data-pcv2-gift-preview]');

      if (hasContent) {
        if (actionText) actionText.textContent = 'Edit';
        if (preview) preview.classList.add('is-visible');

        // Update preview content
        const greetingEl = preview?.querySelector('[data-pcv2-preview-greeting]');
        const messageEl = preview?.querySelector('[data-pcv2-preview-message]');
        const signatureEl = preview?.querySelector('[data-pcv2-preview-signature]');

        if (greetingEl) greetingEl.textContent = greeting;
        if (messageEl) messageEl.textContent = message;
        if (signatureEl) signatureEl.textContent = signature;
      } else {
        if (actionText) actionText.textContent = 'Edit';
        if (preview) preview.classList.remove('is-visible');
      }
    },

    updateNotesCard() {
      const card = document.querySelector('[data-pcv2-card="notes"]');
      if (!card) return;

      const notes = NotesModule.text();
      const hasContent = notes && notes.trim();

      const actionText = card.querySelector('[data-pcv2-notes-action-text]');
      const preview = card.querySelector('[data-pcv2-notes-preview]');

      if (hasContent) {
        if (actionText) actionText.textContent = 'Edit';
        if (preview) preview.classList.add('is-visible');

        // Update preview content
        const notesEl = preview?.querySelector('[data-pcv2-preview-notes]');
        if (notesEl) notesEl.textContent = notes;
      } else {
        if (actionText) actionText.textContent = 'Edit';
        if (preview) preview.classList.remove('is-visible');
      }
    }
  };

  // ==========================================================================
  // Sidebar Updater
  // ==========================================================================
  const SidebarUpdater = {
    // Totals for the selection. The cart side (items total, discounts) is what Liquid
    // rendered with the page.
    update() {
      const shippingRow = document.querySelector('[data-pcv2-shipping-row]');
      const shippingPrice = document.querySelector('[data-pcv2-shipping-price]');
      const shippingOriginal = document.querySelector('[data-pcv2-shipping-original]');
      const deliveryTypeLabel = document.querySelector('[data-pcv2-delivery-type-label]');
      const totalEl = document.querySelector('[data-pcv2-total]');
      const savingsBanner = document.querySelector('[data-pcv2-savings-banner]');
      const savingsAmounts = document.querySelectorAll('[data-pcv2-savings-amount]');
      const savingsLine = document.querySelector('[data-pcv2-savings-line]');
      const stickyTotal = document.querySelector('[data-pcv2-sticky-total]');
      const sidebar = document.querySelector('[data-pcv2-sidebar]');
      const state = PreCheckoutV2.state;

      // No date, and Continue goes on without one (no day suits the cart, or the date
      // check failed at start-up): checkout charges the standard rate, Prime rules included,
      // so the summary shows exactly that - as standard delivery, "date to arrange"
      const arranging = !state.deliveryDate && (ExtrasDateCheck._noFit || PreCheckoutV2.checkFailed);
      const deliveryType = arranging ? 'standard' : state.deliveryType;
      const currentPrice = arranging ? DeliveryOptionsModule.standardRate() : state.deliveryPrice;

      // Update delivery type label from translations
      if (deliveryTypeLabel && sidebar && deliveryType) {
        var typeKey = deliveryType;
        var labelAttr = 'deliveryLabelStandard'; // default
        if (typeKey === 'same_day') {
          labelAttr = 'deliveryLabelSameDay';
        } else if (typeKey === 'same_day_surge') {
          labelAttr = 'deliveryLabelSameDaySurge';
        } else if (typeKey === 'surge_pricing') {
          labelAttr = 'deliveryLabelSurge';
        }
        var label = arranging
          ? sidebar.dataset.deliveryLabelToArrange || 'Standard Delivery (date to arrange)'
          : sidebar.dataset[labelAttr] || 'Delivery';
        deliveryTypeLabel.textContent = label;
      }

      // Calculate base price (what non-Prime pays)
      const basePrice = this.calculateBasePrice(deliveryType);
      // No date chosen: no delivery amount anywhere (row, total, savings). A price of 0
      // would otherwise read as FREE delivery and as a saving of the full base rate.
      const hasDate = !!state.deliveryDate || arranging;

      // Update shipping price with strikethrough if discounted
      if (shippingPrice && !hasDate) {
        shippingPrice.textContent = '-';
        shippingPrice.classList.remove('pcv2-delivery-final--highlighted');
        if (shippingOriginal) shippingOriginal.style.display = 'none';
      } else if (shippingPrice) {
        if (currentPrice === 0) {
          shippingPrice.textContent = PreCheckoutV2.config.typeLabels.free;
          shippingPrice.classList.add('pcv2-delivery-final--highlighted');
        } else {
          shippingPrice.textContent = fmt(currentPrice);
          shippingPrice.classList.remove('pcv2-delivery-final--highlighted');
        }

        // Show strikethrough original price if discounted
        if (shippingOriginal && currentPrice < basePrice) {
          shippingOriginal.textContent = fmt(basePrice);
          shippingOriginal.style.display = 'inline';
          shippingPrice.classList.add('pcv2-delivery-final--highlighted');
        } else if (shippingOriginal) {
          shippingOriginal.style.display = 'none';
        }
      }

      // Total savings = delivery savings + cart-level discounts + Prime product discounts
      // (the last two calculated in Liquid)
      const deliverySavings = hasDate ? basePrice - currentPrice : 0;
      const cartSavings = sidebar
        ? (parseInt(sidebar.dataset.cartDiscounts, 10) || 0) + (parseInt(sidebar.dataset.primeProductSavings, 10) || 0)
        : 0;
      const totalSavings = deliverySavings + cartSavings;
      const hasSavings = totalSavings > 0;

      // Update all savings amounts (banner + sticky footer)
      savingsAmounts.forEach(function(el) {
        el.textContent = fmt(totalSavings);
      });
      if (savingsBanner) savingsBanner.style.display = hasSavings ? 'flex' : 'none';
      if (savingsLine) savingsLine.style.display = hasSavings ? 'block' : 'none';

      // Update total (sidebar + sticky footer)
      const total = DeliveryOptionsModule._cachedCartTotal + (hasDate ? currentPrice : 0);
      if (totalEl) totalEl.textContent = fmt(total);
      if (stickyTotal) stickyTotal.textContent = fmt(total);
    },

    calculateBasePrice(type) {
      // Base price = what a non-Prime customer would pay (for showing savings)
      const config = PreCheckoutV2.config;
      const deliveryType = type || 'standard';
      const isSurge = deliveryType.includes('surge');
      const isSameDay = deliveryType.includes('same_day');

      if (isSameDay) {
        return isSurge ? config.surgeSameDayRate : config.sameDayRate;
      }
      return isSurge ? config.surgeStandardRate : config.standardRate;
    }
  };

  // ==========================================================================
  // Cart Sync - after ANY cart change the server renders the page again
  // ==========================================================================
  // The server-rendered page is the only source of truth for the cart: its lines and
  // totals, the availability payload, the needs-input card, the stored attributes. So a
  // cart change made from this page (Save / Remove), made in the cart drawer, or made
  // while the page sat in the back-forward cache always ends in a full reload, and the
  // normal start-up pass validates everything from scratch. Nothing is patched in place.
  const CartSync = {
    // Every cart request of this page is given up after this long. Accepted residual:
    // giving up does not stop the request. If the server commits it later than a full
    // page reload plus the shopper's next pick, it can still overwrite that newer write.
    // Cart attributes have no server-side ordering, so no client code can rule this out;
    // the page only keeps the window small (a failed Continue reloads, never retries).
    DEADLINE_MS: 10000,
    FLASH_KEY: 'preCheckoutV2Flash',

    // Page lock: a cart request is in flight or a reload is on its way. While it is held
    // nothing else starts - no cart change, no selection change, no Continue. Counted,
    // because holders overlap (a drawer event during the start-up clear, say).
    _locks: 0,
    _staleReloads: 0, // reloads in a row that Continue asked for because the cart was not the rendered one

    get busy() {
      return this._locks > 0;
    },

    lock(on) {
      this._locks = Math.max(0, this._locks + (on === false ? -1 : 1));
      const app = document.querySelector('.pre-checkout-v2');
      if (app) app.setAttribute('aria-busy', String(this.busy)); // CSS dims and disarms the page
      CheckoutHandler.setLoading(this.busy);                     // both Continue buttons show it
      // ...and the status region says it, until the lock is gone (what report() had put there
      // is over: it is not written back later)
      if (this.busy) this._status = null;
      const status = document.querySelector('[data-pcv2-status]');
      if (status && (this.busy || status.textContent === EX_TXT.updating)) status.textContent = this.busy ? EX_TXT.updating : '';
    },

    root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    },

    // Aborts whatever listens to its signal once DEADLINE_MS have passed
    deadline() {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.DEADLINE_MS);
      return { signal: controller.signal, clear: () => clearTimeout(timer) };
    },

    // One cart request (a POST when there is a body). Rejects when it failed, was answered
    // with an error status, or ran out of time. Pass a signal to share one deadline.
    async send(path, body, signal) {
      const own = signal ? null : this.deadline();
      try {
        const res = await fetch(this.root() + path, {
          method: body ? 'POST' : 'GET',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: body ? JSON.stringify(body) : undefined,
          signal: signal || own.signal
        });
        if (!res.ok) throw new Error(path + ' ' + res.status);
        return await res.json();
      } finally {
        if (own) own.clear();
      }
    },

    /**
     * A cart change from this page: lock -> the request(s) under ONE deadline -> reload.
     * The reload happens whatever the outcome (done, failed, timed out): a failure is
     * ambiguous - Shopify may have committed the change and only the answer was lost -
     * and only a fresh render says what the cart is. `flash` travels through the reload
     * so the next load can say what happened and put focus back (afterReload).
     * @param {Object} flash - { action: 'save' | 'remove' | 'extra', key, title, ... }
     * @param {Function} change - (signal) => promise of the request(s)
     */
    async mutate(flash, change) {
      if (this.busy) return;
      this.lock();
      const deadline = this.deadline();
      let result = 'done';
      try {
        await change(deadline.signal);
      } catch (err) {
        result = deadline.signal.aborted ? 'timeout' : 'failed';
        console.warn('[CartSync] Cart change ' + result + ':', err);
      }
      deadline.clear();
      this.reload(Object.assign(flash, { result: result }));
    },

    // Render the page again. What only the browser knows goes along: the selection, gift
    // message and notes (session state), the text typed into the fix card and, after a
    // cart change, how it ended. A Continue still waiting for its answer is cancelled:
    // whatever it gets back must neither go to checkout nor write again.
    reload(flash) {
      CheckoutHandler._attempt += 1;
      this.lock();
      StateManager.save();
      try {
        sessionStorage.setItem(this.FLASH_KEY, JSON.stringify(Object.assign({ typed: NeedsInputModule.typed() }, flash)));
      } catch (e) {
        console.warn('[CartSync] Could not keep the outcome for the next load:', e);
      }
      window.location.reload();
    },

    // First thing after the start-up pass: restore the typed text, then report the change
    // the reload ended. WHAT is reported is read off the fresh page, not off the stored
    // result: a request that failed or timed out may still have been committed. And only
    // when the page is unambiguous about it - otherwise the message is a neutral one.
    afterReload() {
      let flash = null;
      try {
        flash = JSON.parse(sessionStorage.getItem(this.FLASH_KEY) || 'null');
        sessionStorage.removeItem(this.FLASH_KEY);
      } catch (e) {
        console.warn('[CartSync] Could not read the outcome of the last load:', e);
      }
      flash = flash || {};
      if (flash.action) console.log('[CartSync] Reloaded after', flash.action, flash.result);
      this._staleReloads = flash.stale || 0;
      NeedsInputModule.restoreTyped(flash);
      if (flash.action === 'extra') {
        ExtrasDateCheck.afterRemove(flash);
      } else if (flash.action) {
        NeedsInputModule.afterChange(flash);
      } else {
        // The cart changed under the page (a stale Continue, the drawer): say so, the
        // total may have moved. And when the change took the extra away, the date it had
        // pushed out is back.
        const changed = flash.stale || flash.changed ? EX_TXT.cartUpdated : '';
        const kept = this.keptText();
        if (changed || kept) this.report(changed, kept);
      }
      // A Continue that could not be confirmed reloaded the page: it says so here, and the
      // next Continue takes the way that needs no answer (CheckoutHandler.submitForm)
      if (flash.saveFailed) {
        NoticeManager.show('error', { message: CheckoutHandler.saveError() });
        CheckoutHandler.afterSaveFailed();
      }
    },

    // Is this cart JSON another cart than the one the page was rendered for? Compared is
    // only what is the same data on both sides: the ordered list of line key : quantity
    // (Liquid item.key / item.quantity in #cart-minimum-policy, key / quantity here).
    // Nothing computed (totals, prices), so the two cannot disagree about one cart.
    // False when it cannot be told - Continue then marks its write unverified
    // (fingerprint() is null), so a missing fingerprint never passes as "the same cart".
    differs(cart) {
      const rendered = this.fingerprint();
      if (rendered === null || !cart || !cart.items) return false;
      return cart.items.map((item) => item.key + ':' + item.quantity + '|').join('') !== rendered;
    },

    // Are this cart JSON's lines priced otherwise than the cart the page was rendered for? A
    // repricing on the server keeps line keys and quantities (differs() cannot see it), but
    // delivery - and the Prime threshold - was priced on the rendered amounts. Compared is
    // the item subtotal (after line-level discounts, without shipping, taxes or cart-level
    // discounts - shopify.dev Liquid cart.items_subtotal_price, Ajax items_subtotal_price):
    // NOT total_price, which Shopify inflates in POST answers with the shipping of an
    // earlier checkout of the same cart (Staging B-R1 / S5 N6: update.js 959900, rendered
    // and GET /cart.js 920000). A change of a cart-level discount alone is not seen; the
    // store has none (automatic or code) today. False when it cannot be told: no rendered
    // subtotal (Continue then marks its write unverified), or none in the answer.
    repriced(cart) {
      const rendered = this.renderedSubtotal();
      if (rendered === null || !cart || typeof cart.items_subtotal_price !== 'number') return false;
      return cart.items_subtotal_price !== rendered;
    },

    // The item subtotal the page was rendered with (Liquid cart.items_subtotal_price), or null
    renderedSubtotal() {
      const container = document.querySelector('.pcv2-container');
      const subtotal = parseInt(container && container.dataset.cartItemsSubtotal, 10);
      return isNaN(subtotal) ? null : subtotal;
    },

    // The rendered cart's line list (#cart-minimum-policy), or null when it is missing or unreadable
    fingerprint() {
      const rendered = window.DDMAvailability && window.DDMAvailability.parseJSONScript('cart-minimum-policy');
      return rendered && typeof rendered.cart_fingerprint === 'string' ? rendered.cart_fingerprint : null;
    },

    // Say it: in the status region for screen readers, and as a toast for everyone else.
    // `message` is about the cart (what just happened); `kept`, the date an extra had pushed
    // out and that is back, is about the selection - in the toast it is the selection
    // sentence, so a later change of the selection takes it away and keeps the cart outcome.
    report(message, kept) {
      this._status = { cart: message || '', kept: kept || '' };
      this.writeStatus();
      if (message) NoticeManager.show('info', { message: message, silent: true });
      if (kept) NoticeManager.show('date_kept', { message: kept, silent: true });
    },

    // What report() put in the status region: the cart outcome, then the kept date. Kept
    // apart, like the two sentences of the toast, so the region never says more than is true.
    _status: null,

    writeStatus() {
      const el = document.querySelector('[data-pcv2-status]');
      const text = [this._status.cart, this._status.kept].filter(Boolean).join(' ');
      if (el) requestAnimationFrame(() => { el.textContent = text; });
    },

    // A real change of the selection (NoticeManager.dropSelectionNotices): the kept date no
    // longer holds. The cart outcome stays - whether or not the toast is still open.
    dropKeptStatus() {
      if (!this._status || !this._status.kept) return;
      this._status.kept = '';
      this.writeStatus();
    },

    // "Your delivery date is kept: <date>." when this load brought that date back, else ''
    keptText() {
      return StateManager.keptDate ? fill(EX_TXT.dateKept, { date: ExtrasDateCheck.dateText(StateManager.keptDate) }) : '';
    }
  };

  // ==========================================================================
  // Needs-input guard - lines that lost required shopper input (e.g. "Buy again")
  // ==========================================================================
  // pcv2-card-needs-input.liquid renders one line per cart line missing its
  // personalisation / option / photo, at the top of the page. It never blocks Continue: the
  // shopper can add the details, remove the line, or go on - our team then contacts them
  // for the details (ops flag: the Flow alert on personalised lines without properties).
  const NeedsInputModule = {
    _bound: false,
    // EN fallbacks; init() reads the localized strings from the slot's data-txt-* attributes
    txt: {
      saved: 'Details saved for [title].',
      removed: '[title] removed from your order.',
      error: "Couldn't update your cart. Please try again.",
      saveError: "We couldn't save this. Try again, or continue and our team will contact you."
    },

    init() {
      const slot = document.querySelector('[data-pcv2-fix-slot]');
      if (!slot || this._bound) return;
      this._bound = true;
      [['saved', 'saved'], ['removed', 'removed'], ['error', 'error'], ['saveError', 'save-error']].forEach(([key, attr]) => {
        const value = slot.getAttribute('data-txt-' + attr);
        if (value) this.txt[key] = value;
      });

      slot.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-pcv2-fix-save], [data-pcv2-fix-remove]');
        const line = btn && btn.closest('[data-pcv2-fix-line]');
        if (!line || CartSync.busy) return;
        if (btn.hasAttribute('data-pcv2-fix-save')) {
          this.save(line);
        } else {
          this.remove(line);
        }
      });
      const onEdit = (e) => {
        const field = e.target.closest('[data-pcv2-fix-field]');
        if (!field) return;
        this.setFieldError(field, false);
        this.updateCount(field);
      };
      slot.addEventListener('input', onEdit);
      slot.addEventListener('change', onEdit);
    },

    lines() {
      return Array.from(document.querySelectorAll('[data-pcv2-fix-line]'));
    },

    focusLine(line) {
      const target = line.querySelector('[data-pcv2-fix-field]') || line;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      line.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
      target.focus({ preventScroll: true });
    },

    setFieldError(field, show) {
      const error = field.parentNode.querySelector('[data-pcv2-fix-field-error]');
      if (error) error.hidden = !show;
      if (show) {
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.removeAttribute('aria-invalid');
      }
    },

    save(line) {
      const values = {};
      let firstInvalid = null;
      line.querySelectorAll('[data-pcv2-fix-field]').forEach((field) => {
        const value = field.value.trim();
        if (value) {
          values[field.dataset.pcv2FixField] = value;
        } else {
          this.setFieldError(field, true);
          firstInvalid = firstInvalid || field;
        }
      });
      if (firstInvalid) {
        firstInvalid.focus();
        return;
      }

      const key = line.dataset.key;
      CartSync.mutate(this.flash('save', line), async (signal) => {
        // change.js replaces the whole properties map: read the line as the cart has it
        // now and merge, so every existing property (and the quantity) survives
        const cart = await CartSync.send('cart.js', null, signal);
        const item = (cart.items || []).find((i) => i.key === key);
        if (!item) throw new Error('line ' + key + ' not in cart');
        await CartSync.send('cart/change.js', {
          id: key, quantity: item.quantity, properties: Object.assign({}, item.properties || {}, values)
        }, signal);
      });
    },

    remove(line) {
      CartSync.mutate(this.flash('remove', line), (signal) => CartSync.send('cart/change.js', { id: line.dataset.key, quantity: 0 }, signal));
    },

    // What travels through the reload for a Save / Remove of this line
    flash(action, line) {
      return {
        action: action, key: line.dataset.key, variant: line.dataset.variantId,
        title: line.dataset.title || '', index: this.lines().indexOf(line)
      };
    },

    // What the shopper typed / picked, by line property name
    fieldValues(line) {
      const values = {};
      line.querySelectorAll('[data-pcv2-fix-field]').forEach((field) => {
        values[field.dataset.pcv2FixField] = field.value;
      });
      return values;
    },

    setFieldValues(line, values) {
      line.querySelectorAll('[data-pcv2-fix-field]').forEach((field) => {
        const value = values[field.dataset.pcv2FixField];
        if (!value) return;
        field.value = value;
        this.updateCount(field);
      });
    },

    updateCount(field) {
      const count = field.parentNode.querySelector('[data-pcv2-fix-count]');
      if (count) count.textContent = field.value.length + ' / ' + (field.maxLength > 0 ? field.maxLength : 256);
    },

    // Every line's unsaved input, for the reload
    typed() {
      return this.lines().map((line) => ({ key: line.dataset.key, values: this.fieldValues(line) }));
    },

    line(key) {
      return this.lines().find((line) => line.dataset.key === key) || null;
    },

    // After a reload: put each draft back on the line with exactly the same key. A key
    // that is gone (saved, removed, or changed) takes its draft with it: a draft on
    // another line would be the wrong text on the wrong item.
    restoreTyped(flash) {
      (flash.typed || []).forEach((entry) => {
        const line = this.line(entry.key);
        if (line) this.setFieldValues(line, entry.values);
      });
    },

    // After the reload a Save / Remove ended in. What is said follows only from what the
    // fresh page shows beyond doubt:
    //   the very same key still needs input (Save) / is still in the cart (Remove)
    //     -> it did not happen: error on that line, focus on the retry
    //   the key is gone and no line of the same product needs input under a key the
    //   page did not have before
    //     -> it happened (also when the request failed or timed out: it had been
    //        committed and only its answer was lost)
    //   anything else (line keys moved meanwhile)
    //     -> neutral message: no error, no claim, no focus on a button
    afterChange(flash) {
      const line = this.line(flash.key);
      const remove = flash.action === 'remove';
      const inCart = remove ? ExtrasDateCheck.inCart(flash.key) : false; // null: cannot be told
      if (line && (!remove || inCart)) {
        const error = line.querySelector('[data-pcv2-fix-error]');
        if (error) {
          error.hidden = false;
          error.textContent = remove ? this.txt.error : this.txt.saveError;
        }
        (line.querySelector(remove ? '[data-pcv2-fix-remove]' : '[data-pcv2-fix-save]') || line).focus();
        return;
      }
      const known = (flash.typed || []).map((entry) => entry.key);
      const moved = this.lines().some((other) => other.dataset.variantId === flash.variant && known.indexOf(other.dataset.key) === -1);
      if (line || moved || (remove && inCart !== false)) {
        CartSync.report(EX_TXT.cartUpdated);
        return;
      }
      CartSync.report(fill(remove ? this.txt.removed : this.txt.saved, { title: flash.title }));
      const remaining = this.lines();
      if (remaining.length) {
        this.focusLine(remaining[Math.min(flash.index, remaining.length - 1)]);
        return;
      }
      const heading = document.querySelector('.pcv2-delivery-v3__title');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }
  };

  // ==========================================================================
  // Extras date help (upsell stage 5)
  // ==========================================================================
  // Upsell extras are cart lines carrying `_upsell_placement` (payload item.upsell).
  // The cart-wide availability already disables every date an extra cannot make; this
  // module only EXPLAINS it and offers to remove the extra:
  //   - under EXPRESS / NEXT-DAY when the option is disabled solely because of extras
  //   - as an alert when a stored delivery date was cleared solely because of extras
  // It never disables anything and never gates Continue.
  // EN fallbacks, overridden by the localized data-* attributes of [data-pcv2-extras-strings]
  // (pcv2-card-delivery-v3.liquid); this script is deferred, so the element is parsed.
  const EX_TXT = Object.assign({
    blockedToday: "[title] can't be delivered today.",
    blockedTomorrow: "[title] can't be delivered tomorrow.",
    blockedHintOne: 'Remove it to unlock this option, or keep it and choose a later date.',
    blockedHintOther: 'Remove them to unlock this option, or keep them and choose a later date.',
    dateLostOne: "[title] can't arrive on [date], the date you chose.",
    dateLostOther: "[title] can't arrive on [date], the date you chose.",
    dateLostHintOne: 'Remove it to keep [date], or choose a new date.',
    dateLostHintOther: 'Remove them to keep [date], or choose a new date.',
    dateReplacedOne: "Delivery is now set to [new_date] because [title] can't arrive on [date].",
    dateReplacedOther: "Delivery is now set to [new_date] because [title] can't arrive on [date].",
    dateReplacedHintOne: 'Remove it to keep [date], or choose another date.',
    dateReplacedHintOther: 'Remove them to keep [date], or choose another date.',
    remove: 'Remove [title]',
    chooseDate: 'Choose a new date',
    chooseAnother: 'Choose another date',
    dismiss: 'Dismiss',
    removeError: "Couldn't remove it. Please try again.",
    cartUpdated: 'Your cart was updated. Check it and press Continue.',
    updating: 'Updating your cart',
    removed: '[title] removed from your order.',
    dateKept: 'Your delivery date is kept: [date].',
    dateSelected: 'Delivery date: [date].'
  }, (document.querySelector('[data-pcv2-extras-strings]') || {}).dataset);

  // Literal token replacement (a title may contain "$&" etc.)
  const fill = (template, values) => Object.keys(values).reduce(
    (text, key) => text.split('[' + key + ']').join(values[key]), template);

  // "A and B" / "A et B" in the page language ("A or B" / "A ou B" with 'disjunction')
  const joinTitles = (titles, type) => (typeof Intl.ListFormat === 'function'
    ? new Intl.ListFormat(_PC_LANG, { style: 'long', type: type || 'conjunction' }).format(titles)
    : titles.join(', '));

  const ExtrasDateCheck = {
    MAX_LISTED: 3,
    _lostDate: null, // DD-MM-YYYY while the "date lost" alert is shown
    _lostKeys: [],   // line keys of the extras that alert names
    _noFit: false,   // the "no delivery date suits every item" alert is shown
    _bound: false,

    init() {
      const card = document.querySelector('[data-pcv2-card="delivery"]');
      if (!card || this._bound) return;
      this._bound = true;

      // Delegated: panels and the alert are redrawn whenever the option states are
      card.addEventListener('click', (e) => {
        if (CartSync.busy) return; // the page is locked
        const removeBtn = e.target.closest('[data-pcv2-extras-remove]');
        if (removeBtn) {
          this.remove(removeBtn);
          return;
        }
        const chooseBtn = e.target.closest('[data-pcv2-extras-choose]');
        if (chooseBtn) {
          ModalController.open('delivery', chooseBtn);
          return;
        }
        // Picking EXPRESS / NEXT-DAY is choosing a new date: the alert is moot
        const option = e.target.closest('[data-pcv2-option]');
        if (option && option.dataset.pcv2Option !== 'custom' &&
            !option.classList.contains('pcv2-delivery-option--disabled')) {
          this.clearLost();
        }
      });
    },

    titleOf(extra) {
      return extra.productTitle || extra.title || '';
    },

    dateText(dmy) {
      return DateUtils.toSentence(dmy);
    },

    // Extras that ALONE make this date undeliverable (see DDMAvailability.findBlockingExtras)
    blockers(date) {
      const DDM = window.DDMAvailability;
      if (!date || !DDM || typeof DDM.findBlockingExtras !== 'function') return [];
      const payload = DDM.parseJSONScript('pcv2-availability-config');
      if (!payload) return [];
      return DDM.findBlockingExtras(payload, date, new Date(), {
        tzOffsetHours: PreCheckoutV2.config.tzOffsetHours
      }).slice(0, this.MAX_LISTED);
    },

    // Sentences, one hint, then the actions (all the same button style: none of them is
    // the "secondary" one)
    buildBody(lines, hint, extras, chooseLabel) {
      const body = document.createElement('div');
      body.className = 'pcv2-extras__body';
      body.setAttribute('data-pcv2-notice-text', '');

      lines.forEach((text) => {
        const line = document.createElement('p');
        line.className = 'pcv2-extras__title';
        line.textContent = text;
        body.appendChild(line);
      });

      if (hint) {
        const hintEl = document.createElement('p');
        hintEl.className = 'pcv2-extras__hint';
        hintEl.textContent = hint;
        body.appendChild(hintEl);
      }

      const actions = document.createElement('div');
      actions.className = 'pcv2-extras__actions';
      if (chooseLabel) {
        const choose = document.createElement('button');
        choose.type = 'button';
        choose.className = 'pcv2-extras__btn';
        choose.setAttribute('data-pcv2-extras-choose', '');
        choose.textContent = chooseLabel;
        actions.appendChild(choose);
      }
      extras.forEach((extra) => {
        const title = this.titleOf(extra);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'pcv2-extras__btn';
        remove.setAttribute('data-pcv2-extras-remove', '');
        remove.dataset.key = extra.key;
        remove.dataset.title = title;
        remove.textContent = fill(EX_TXT.remove, { title: title });
        actions.appendChild(remove);
      });
      body.appendChild(actions);

      const error = document.createElement('p');
      error.className = 'pcv2-extras__error';
      error.setAttribute('role', 'alert');
      error.setAttribute('data-pcv2-extras-error', '');
      error.hidden = true;
      body.appendChild(error);

      return body;
    },

    // Called at the end of DeliveryOptionsModule.updateOptionStates()
    run() {
      const today = DateUtils.getGMT4Today();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.renderOption('fast', !DeliveryOptionsModule._sameDayAvailable, today, EX_TXT.blockedToday);
      this.renderOption('tomorrow', !DeliveryOptionsModule._tomorrowAvailable, tomorrow, EX_TXT.blockedTomorrow);
    },

    renderOption(type, disabled, date, sentence) {
      const option = document.querySelector('[data-pcv2-option="' + type + '"]');
      if (!option) return;
      const input = option.querySelector('input');
      const panelId = 'pcv2ExtrasHelp-' + type;
      let panel = document.getElementById(panelId);
      // An extra the "date lost" alert already names (with its own Remove) is not offered
      // a second time here; the option then keeps its generic notice
      const extras = (disabled ? this.blockers(DateUtils.toISO(date)) : [])
        .filter((extra) => this._lostKeys.indexOf(extra.key) === -1);

      if (!extras.length) {
        if (panel) panel.remove();
        if (input) input.removeAttribute('aria-describedby');
        return;
      }

      // The panel replaces the generic in-option notice (updateOptionStates re-shows
      // that notice on every run, so there is nothing to restore here)
      const notice = option.querySelector('.pcv2-option__notice');
      if (notice) notice.style.display = 'none';

      if (!panel) {
        // Sibling of the <label>, not a child: a button may not live inside a label,
        // and the disabled option is dimmed
        panel = document.createElement('div');
        panel.id = panelId;
        panel.className = 'pcv2-extras-help';
        panel.setAttribute('data-pcv2-extras-help', type);
        option.insertAdjacentElement('afterend', panel);
      }
      if (input) input.setAttribute('aria-describedby', panelId);

      panel.textContent = '';
      panel.appendChild(this.buildBody(
        extras.map((extra) => fill(sentence, { title: this.titleOf(extra) })),
        extras.length === 1 ? EX_TXT.blockedHintOne : EX_TXT.blockedHintOther,
        extras,
        null
      ));
    },

    // Called by StateManager.explainLost() when the date the page opened with was rejected.
    // Returns true when extras alone caused it (the alert then replaces the generic notice).
    showLost(lostDate) {
      const extras = this.blockers(lostDate);
      if (!extras.length) return false;
      this._lostDate = lostDate;
      this.renderLost(extras);
      return true;
    },

    // The wording follows what the page is doing RIGHT NOW with the selection:
    //   a date is selected -> "Delivery is now set to <that date> because <extra> can't
    //                          arrive on <old date>" + Choose another date
    //   nothing selected   -> "<extra> can't arrive on <old date>" + Choose a new date
    renderLost(extras) {
      const current = PreCheckoutV2.state.deliveryDate;
      const replaced = !!current && current !== this._lostDate;
      const form = extras.length === 1 ? 'One' : 'Other';
      const values = {
        title: joinTitles(extras.map((extra) => this.titleOf(extra))),
        date: this.dateText(this._lostDate),
        new_date: replaced ? this.dateText(current) : ''
      };
      this.insertAlert(this.buildBody(
        [fill(EX_TXT[(replaced ? 'dateReplaced' : 'dateLost') + form], values)],
        fill(EX_TXT[(replaced ? 'dateReplacedHint' : 'dateLostHint') + form], values),
        extras,
        replaced ? EX_TXT.chooseAnother : EX_TXT.chooseDate
      ), extras);
    },

    // The alert above the options (one at a time; it replaces the inline notice too)
    insertAlert(body, extras) {
      const container = document.querySelector('[data-pcv2-delivery-options]');
      const existing = document.querySelector('[data-pcv2-extras-alert]');
      if (existing) existing.remove();
      if (!container) return;
      NoticeManager.hideInline();

      const alert = document.createElement('div');
      alert.className = 'pcv2-validation-notice pcv2-validation-notice--invalid pcv2-extras-alert';
      alert.setAttribute('role', 'alert');
      alert.setAttribute('data-pcv2-extras-alert', '');
      const icon = NoticeManager.createIcon('invalid');
      icon.setAttribute('class', 'pcv2-notice__icon');
      icon.setAttribute('aria-hidden', 'true');
      alert.appendChild(icon);
      alert.appendChild(body);

      container.parentNode.insertBefore(alert, container);
      this._lostKeys = extras.map((extra) => extra.key);
      // Redraw the options: those items leave the panels, the generic notices come back
      DeliveryOptionsModule.updateOptionStates();
    },

    // No day suits every item in the cart (nothing can be selected, the calendar would be
    // empty). Name the items that rule the days out, each with its Remove, and say that
    // Continue still goes through: our team then arranges the date with the shopper.
    showNoFit() {
      const items = this.everyDayBlockers();
      this._noFit = true;
      this.insertAlert(this.buildBody([this.noFitText(items.map((item) => this.titleOf(item)))], null, items, null), items);
      SidebarUpdater.update(); // the standard delivery checkout will charge, "date to arrange"
    },

    noFitText(titles) {
      const isFr = _PC_LANG.indexOf('fr') === 0;
      const lead = isFr
        ? 'Aucune date de livraison ne convient à tous les articles de votre panier. '
        : 'No delivery date suits every item in your cart. ';
      if (!titles.length) {
        return lead + (isFr
          ? 'Continuez et notre équipe vous contactera pour organiser la livraison.'
          : 'Continue and our team will contact you to arrange delivery.');
      }
      return lead + (isFr
        ? 'Retirez ' + joinTitles(titles, 'disjunction') + ', ou continuez et notre équipe vous contactera pour organiser la livraison.'
        : 'Remove ' + joinTitles(titles, 'disjunction') + ', or continue and our team will contact you to arrange delivery.');
    },

    // The lines whose own dates rule out every day the rest of the cart could have: the
    // per-item check of the extras (findBlockingExtras), run with each line in turn as the
    // "extra" over the days the calendar offers. A line is never blamed for days the other
    // lines rule out anyway, so a single line, or two lines that each rule out everything,
    // are not named.
    everyDayBlockers() {
      const DDM = window.DDMAvailability;
      const payload = DDM && typeof DDM.findBlockingExtras === 'function' ? DDM.parseJSONScript('pcv2-availability-config') : null;
      const items = (payload && payload.items) || [];
      if (items.length < 2) return [];
      const days = [];
      const day = DateUtils.getGMT4Today();
      const last = new Date(DeliveryOptionsModule._minDate || day);
      last.setDate(last.getDate() + 89);
      for (; day <= last; day.setDate(day.getDate() + 1)) days.push(DateUtils.toISO(day));
      const now = new Date();
      const options = { tzOffsetHours: PreCheckoutV2.config.tzOffsetHours };
      return items.filter((item) => {
        const probe = Object.assign({}, payload, {
          items: items.map((other) => Object.assign({}, other, { upsell: other === item ? 'probe' : '' }))
        });
        return days.some((iso) => DDM.findBlockingExtras(probe, iso, now, options).length > 0);
      }).slice(0, this.MAX_LISTED);
    },

    // The shopper chose another date: the alert, and the generic "date could not be kept"
    // notice, have done their job. They stay as a confirmation (NoticeManager.resolve) of
    // one true sentence, the date now selected - no offer to keep the old date, no Remove: that
    // action would no longer do what it said. The alert keeps its extras (_lostKeys), so
    // no panel appears under an option either.
    clearLost() {
      const date = PreCheckoutV2.state.deliveryDate;
      const text = date ? fill(EX_TXT.dateSelected, { date: this.dateText(date) }) : '';
      NoticeManager.resolveInline(text);
      this._lostDate = null;
      const alert = document.querySelector('[data-pcv2-extras-alert]');
      if (alert) {
        // The delivery modal returns focus to its trigger: not to a button that is gone now
        if (ModalController.previousFocus && alert.contains(ModalController.previousFocus)) {
          ModalController.previousFocus = this.selectedInput();
        }
        NoticeManager.resolve(alert, text);
      }
      if (PreCheckoutV2.state.lostDate) {
        PreCheckoutV2.state.lostDate = null; // nothing to restore on a later load
        StateManager.save();
      }
    },

    selectedInput() {
      return document.querySelector('[data-pcv2-option].pcv2-delivery-option--selected input');
    },

    HOSTS: '[data-pcv2-extras-help], [data-pcv2-extras-alert]',

    // Inline error of a panel / the alert. Returns false when there is nowhere to show it.
    setError(host, on) {
      const errorEl = host ? host.querySelector('[data-pcv2-extras-error]') : null;
      if (!errorEl) return false;
      errorEl.hidden = !on;
      errorEl.textContent = on ? EX_TXT.removeError : '';
      return true;
    },

    // Is this line in the cart the page was rendered for? null when that cannot be told
    // (no or unreadable availability data): then nothing is claimed about it
    inCart(key) {
      const DDM = window.DDMAvailability;
      const payload = DDM ? DDM.parseJSONScript('pcv2-availability-config') : null;
      if (!payload || !Array.isArray(payload.items)) return null;
      return payload.items.some((item) => item.key === key);
    },

    remove(btn) {
      const key = btn.dataset.key;
      const host = btn.closest(this.HOSTS);
      CartSync.mutate({
        action: 'extra', key: key, title: btn.dataset.title || '',
        option: host ? host.getAttribute('data-pcv2-extras-help') : null // 'fast' | 'tomorrow', null for the alert
      }, (signal) => CartSync.send('cart/change.js', { id: key, quantity: 0 }, signal));
    },

    // After the reload a Remove ended in. With the extra gone, the start-up pass has
    // already brought back the date it had pushed out (StateManager.keptDate).
    afterRemove(flash) {
      const inCart = this.inCart(flash.key);
      if (inCart === null) {
        CartSync.report(EX_TXT.cartUpdated); // the page cannot tell whether it is gone
        return;
      }
      if (inCart) {
        // Still in the cart: offer the retry where the extra is listed now (same panel first)
        const again = '[data-pcv2-extras-remove][data-key="' + flash.key + '"]';
        const retry = (flash.option && document.querySelector('[data-pcv2-extras-help="' + flash.option + '"] ' + again)) ||
          document.querySelector(again);
        if (!this.setError(retry ? retry.closest(this.HOSTS) : null, true)) {
          NoticeManager.show('error', { message: EX_TXT.removeError });
        }
        if (retry) retry.focus();
        return;
      }
      // Removed - also when the request failed or timed out and the payload has no such
      // line all the same: it had been committed, only its answer was lost
      CartSync.report(fill(EX_TXT.removed, { title: flash.title }), CartSync.keptText());
      this.focusAfterRemove(flash.option);
    },

    // The button that had focus is gone: move to the next thing the shopper acts on
    focusAfterRemove(optionType) {
      const find = (selector) => document.querySelector(selector);
      const next = optionType
        // another extra still listed under the same option, else the option now unlocked
        ? (find('[data-pcv2-extras-help="' + optionType + '"] [data-pcv2-extras-remove]') ||
           find('[data-pcv2-option="' + optionType + '"] input:not(:disabled)'))
        // another extra still listed in the alert
        : find('[data-pcv2-extras-alert] [data-pcv2-extras-remove]');
      // ...else the selected option, else (selection was cleared) the first one still open
      const target = next || this.selectedInput() || find('[data-pcv2-option] input:not(:disabled)');
      if (target) target.focus();
    }
  };

  // ==========================================================================
  // Checkout Handler
  // ==========================================================================
  const CheckoutHandler = {
    // Bumped by every Continue and by every reload (a cart change, the back-forward cache):
    // a continuation that belongs to an older attempt does nothing.
    _attempt: 0,
    posting: false, // a Continue is waiting for its answer
    // The last Continue in this tab could not be confirmed and reloaded the page: the next
    // one is a plain form POST to /cart (submitForm), which needs no answer
    viaForm: false,

    init() {
      // Desktop checkout button
      const checkoutBtn = document.querySelector('[data-pcv2-checkout]');
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.proceed(checkoutBtn);
        });
      }

      // Mobile sticky checkout button
      const stickyCheckoutBtn = document.querySelector('[data-pcv2-checkout-sticky-btn]');
      if (stickyCheckoutBtn) {
        stickyCheckoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.proceed(stickyCheckoutBtn);
        });
      }

      // Back / forward cache: the cart may have changed since this copy of the page was
      // frozen, and a frozen Continue must not finish. Never resume: render it again.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) CartSync.reload({}); // cancels a frozen Continue too
      });
    },

    // Loading state on both Continue buttons
    setLoading(on) {
      ['[data-pcv2-checkout]', '[data-pcv2-checkout-sticky-btn]'].forEach((sel) => {
        const btn = document.querySelector(sel);
        if (!btn) return;
        btn.disabled = on;
        btn.classList.toggle('is-loading', on);
      });
    },

    // [DATE GATE] The date is judged again where it is posted, by the validator every other
    // path uses: the clock may have passed the cutoff (or midnight) since it was chosen.
    // Rejected -> cleared with the reason, and the calendar opens. Still valid but on other
    // terms (type or price) -> shown first, never posted unseen.
    // Returns null = stop here (the reason is on the page), { toArrange: true } = no date
    // suits the cart, {} = post the selected date.
    gate(triggerBtn) {
      const state = PreCheckoutV2.state;
      const terms = state.deliveryType + ':' + state.deliveryPrice;
      DeliveryOptionsModule.localiseQuickDates(); // "Today" / "Tomorrow" are those of now
      DeliveryOptionsModule.computeAvailability();
      const kept = DeliveryOptionsModule.revalidateSelectedDate();
      if (!kept || terms !== state.deliveryType + ':' + state.deliveryPrice) {
        SidebarUpdater.update();
        StateManager.save();
        if (kept) {
          NoticeManager.show('rate_changed', { message: NoticeManager.priceNow(state.deliveryPrice, true) });
          return null;
        }
      }

      // No date selected: the calendar, with guidance - unless no day suits the cart (it
      // would be empty). Then the page says so first (start-up does; here, when the clock
      // just took the last day away), and the next Continue goes through without a date:
      // our team arranges it (delivery_date_status = to_arrange).
      if (!state.deliveryDate) {
        if (DeliveryOptionsModule.firstValidDate() !== null) {
          ModalController.open('delivery', triggerBtn);
          // Show error after modal opens
          setTimeout(() => {
            ModalController.showError('delivery', PreCheckoutV2.config.typeLabels.error_select_date);
          }, 150);
          return null;
        }
        if (!ExtrasDateCheck._noFit) {
          ExtrasDateCheck.showNoFit();
          return null;
        }
        return { toArrange: true };
      }
      return {};
    },

    // The date check threw. The date is not re-checked, but the terms it would go with are
    // worked out again with the pure functions of the normal path, none of them the
    // validator: today or not and the cutoff (clock), peak ranges, rate matrix (Prime).
    // New terms are shown first, like any change of terms at Continue (P8). A date the clock
    // rules out, or terms that cannot be worked out either, leave no date to send: it is
    // cleared and Continue goes on as standard delivery, date to arrange.
    // Same returns as gate(); { unverified: true } = post the date, marked unverified.
    failOpen() {
      const state = PreCheckoutV2.state;
      PreCheckoutV2.checkFailed = true;
      if (!state.deliveryDate) {
        SidebarUpdater.update(); // standard delivery, date to arrange
        return { toArrange: true };
      }
      const terms = state.deliveryType + ':' + state.deliveryPrice;
      try {
        if (!DeliveryOptionsModule.clockAllows(state.deliveryDate)) throw new Error('the date has passed, or same-day has closed');
        DeliveryOptionsModule.applyDate(state.deliveryDate); // type and price for the date, as the normal path sets them
      } catch (error) {
        console.error('[CheckoutHandler] No terms for the selected date:', error);
        try {
          DeliveryOptionsModule.clearSelection();
        } catch (clearError) {
          console.error('[CheckoutHandler] Could not clear the selection:', clearError);
        }
        state.deliveryDate = null;
        SidebarUpdater.update();
        StateManager.save();
        NoticeManager.show('rate_changed', { message: this.unconfirmedText() });
        return null;
      }
      if (terms === state.deliveryType + ':' + state.deliveryPrice) return { unverified: true };
      SidebarUpdater.update();
      StateManager.save();
      NoticeManager.show('rate_changed', { message: NoticeManager.priceNow(state.deliveryPrice, true) });
      return null;
    },

    unconfirmedText() {
      return _PC_LANG.indexOf('fr') === 0
        ? 'Nous n\'avons pas pu confirmer votre date de livraison. Continuez et notre équipe vous contactera pour organiser la livraison.'
        : 'We couldn\'t confirm your delivery date. Continue and our team will contact you to arrange delivery.';
    },

    async proceed(clickedBtn) {
      // The page is locked (a cart request, a reload or an earlier Continue is under way):
      // refused, and the buttons show why - whatever the overlay lets through
      if (CartSync.busy || this.posting) return;
      const triggerBtn = clickedBtn || document.querySelector('[data-pcv2-checkout]');
      if (!triggerBtn) return;
      this.showSaveError(false); // the error of the last failed Continue stands until this press
      NoticeManager.dropCartNotice(); // ...and so does a cart message in the toast

      const state = PreCheckoutV2.state;
      let gate;
      try {
        gate = this.gate(triggerBtn);
      } catch (error) {
        // Fail open: Continue never stops on a broken validator (the start-up "Unable to
        // validate cart" case, say)
        console.error('[CheckoutHandler] Date check failed:', error);
        try {
          gate = this.failOpen();
        } catch (failOpenError) {
          console.error('[CheckoutHandler] Could not work out the terms either:', failOpenError);
          gate = { toArrange: true };
        }
      }
      if (!gate) return; // stopped, with the reason on the page
      const toArrange = !!gate.toArrange;

      // The cart changed under two Continues in a row (each reloaded the page): this one
      // goes to checkout whatever the answer, its write marked delivery_date_check =
      // unverified for our team to check the date against the order
      const lastTry = CartSync._staleReloads >= 2;
      // ...also marked when the date check failed (fail open), or when the answer cannot
      // be compared with the rendered cart (no fingerprint, no rendered item subtotal)
      const unverified = lastTry || !!gate.unverified || CartSync.fingerprint() === null || CartSync.renderedSubtotal() === null;

      const attempt = ++this._attempt;
      CartSync.lock();
      this.posting = true; // until the page is left

      try {
        // Determine prime_status for Craftly (priority: legacy > member > cart > none)
        const container = document.querySelector('.pcv2-container');
        const isLegacyPrime = container?.dataset?.isLegacyPrime === 'true';
        const isPrimeMember = container?.dataset?.isPrimeMember === 'true';
        const hasPrimeInCart = container?.dataset?.hasPrimeInCart === 'true';

        let primeStatus = 'none';
        if (isLegacyPrime) {
          primeStatus = 'legacy';
        } else if (isPrimeMember) {
          primeStatus = 'member';
        } else if (hasPrimeInCart) {
          primeStatus = 'cart';
        }

        // Build attributes. Every way to checkout carries delivery_type with its rate and
        // prime_status: the Craftly delivery customization shows only the matching shipping
        // method; without delivery_type checkout offers them all (S4A-03). With no date to
        // give, that is the standard method, and "Delivery Date" is written blank so that
        // Shopify drops a stale one.
        const attributes = {
          'Delivery Date': toArrange ? '' : state.deliveryDate,
          'delivery_type': toArrange ? 'standard' : state.deliveryType,
          'delivery_rate_at_selection': String(toArrange ? DeliveryOptionsModule.standardRate() : state.deliveryPrice),
          'selection_timestamp': new Date().toISOString(),
          'prime_status': primeStatus,
          // Ops flags for customer care (docs/cro/upsell-app/stage5-ops-flags.md). Blank =
          // dropped by Shopify: a normal Continue clears a flag an earlier one left.
          'delivery_date_status': toArrange ? 'to_arrange' : '',
          'delivery_date_check': unverified ? 'unverified' : ''
        };

        // Add cart vertical if detected (gifting/fresh/babybest)
        const cartVertical = container?.dataset?.cartVertical;
        if (cartVertical) {
          attributes['Vertical'] = cartVertical;
        }

        // Gift message. Untouched in this tab, it is the cart's own: no card keys are
        // written and the cart keeps what it has. Edited or erased here - or a gift write
        // was tried from this tab before, which may have gone through - the card keys are
        // written with their current values: all four, empty ones as "", whenever the
        // cart has or may have a gift message to replace; a first gift message writes
        // only the fields that have text.
        if (state.giftTouched || state.giftAttempted) {
          const gift = state.giftMessage;
          const cards = {
            'Card Greeting': gift.greeting, 'Card Message': gift.message,
            'Card Signature': gift.signature, 'Card Occasion': gift.occasion
          };
          const writeAll = state.giftAttempted || GiftModule.stored();
          Object.keys(cards).forEach((key) => {
            if (cards[key] || writeAll) attributes[key] = cards[key] || '';
          });
          state.giftAttempted = state.giftAttempted || Object.keys(cards).some((key) => key in attributes);
        }
        // In the session BEFORE the write goes out: what this Continue is about to try
        // must be known after any reload, whatever becomes of the request
        StateManager.save();

        // After a Continue that could not be confirmed: the same attributes and note, as a
        // form POST that saves the cart and goes to checkout in one navigation
        if (this.viaForm) {
          this.submitForm(attributes, state.orderNotes);
          return; // locked until the browser has left the page
        }

        // Update cart (attributes + note), with the deadline every cart request has. The
        // answer has to carry the delivery attributes just submitted (another writer may
        // have got in between); if not, this counts as a failed write (below).
        const cart = await this.updateCart(attributes, state.orderNotes);
        if (attempt !== this._attempt) return;
        if (!this.carries(cart, attributes)) throw new Error('the cart did not keep the delivery attributes');

        // The answer is the cart as the server has it. Another cart than the one this
        // page was rendered for (changed in another tab, or by a request given up at its
        // deadline that went through after all), or the same lines repriced on the server:
        // the date and the delivery price were judged for the wrong cart. Render the page
        // again - up to twice in a row; the third Continue went out flagged (lastTry) and
        // goes to checkout, so this can neither loop nor strand. (The /cart form after a
        // failed save gets no answer to compare: it goes on as it is.)
        if (!lastTry && (CartSync.differs(cart) || CartSync.repriced(cart))) {
          CartSync.reload({ stale: CartSync._staleReloads + 1 });
          return;
        }

        // Redirect (locale-aware). The page stays locked until it is left.
        window.location.href = theme.routes.home.replace(/\/?$/, '/') + 'checkout';
      } catch (error) {
        if (attempt !== this._attempt) return;
        console.error('[CheckoutHandler] Error:', error);
        // What reached the server is unknown (timeout, network error, an error status,
        // an answer that is not the cart we wrote). The page is never handed back for a
        // retry here - the abandoned write could still land after it. Render the page
        // again from the server's cart; it says the details were not saved (afterReload).
        CartSync.reload({ saveFailed: true });
      }
    },

    saveError() {
      return _PC_LANG.indexOf('fr') === 0
        ? 'Nous n\'avons pas pu enregistrer vos informations de livraison. Appuyez sur Continuer pour réessayer.'
        : 'We couldn\'t save your delivery details. Press Continue to try again.';
    },

    // The page this failed Continue reloaded to: the error stays above both Continue
    // buttons (next to the toast) until the next press, and that press posts a form
    afterSaveFailed() {
      this.viaForm = true;
      this.showSaveError(true);
    },

    showSaveError(on) {
      document.querySelectorAll('[data-pcv2-continue-error]').forEach((el) => {
        el.textContent = on ? this.saveError() : '';
        el.hidden = !on;
      });
    },

    // A plain HTML form POST to /cart, as the theme cart template does it (shopify.dev,
    // themes/architecture/templates/cart: "Proceed to checkout", "Cart attributes", "Cart
    // notes"): Shopify saves attributes[...] and the note, and the `checkout` field sends
    // the shopper on to checkout in the same navigation. Same values as updateCart().
    submitForm(attributes, note) {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = CartSync.root() + 'cart';
      form.hidden = true;
      const field = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };
      const safeAttributes = this.sanitizeAttributes(attributes);
      Object.keys(safeAttributes).forEach((key) => field('attributes[' + key + ']', safeAttributes[key]));
      const safeNote = this.truncateAttribute(note, 5000);
      if (typeof safeNote === 'string') field('note', safeNote); // a draft only, as in updateCart()
      field('checkout', '');
      document.body.appendChild(form);
      form.submit();
    },

    // Does this cart JSON carry the delivery attributes that were submitted? A blank one
    // must be gone (Shopify drops a key written blank).
    carries(cart, attributes) {
      const saved = (cart && cart.attributes) || {};
      return ['Delivery Date', 'delivery_type', 'delivery_rate_at_selection', 'delivery_date_status', 'delivery_date_check']
        .every((key) => !(key in attributes) || (saved[key] || '') === attributes[key]);
    },

    // SEC-C1: Truncate string to max length (defense-in-depth)
    truncateAttribute(value, maxLength) {
      if (typeof value !== 'string') return value;
      return value.length > maxLength ? value.substring(0, maxLength) : value;
    },

    // SEC-C1: Sanitize all cart attributes before API call
    sanitizeAttributes(attributes) {
      var limits = {
        'Card Message': 500,
        'Card Greeting': 100,   // matches HTML maxlength
        'Card Signature': 100,  // matches HTML maxlength
        'Card Occasion': 100,
        'Vertical': 50,
        'Delivery Date': 50,
        'delivery_type': 50,
        'delivery_rate_at_selection': 20,
        'selection_timestamp': 50,
        'prime_status': 20
      };
      var sanitized = {};
      for (var key in attributes) {
        if (attributes.hasOwnProperty(key)) {
          var limit = limits[key] || 255;
          sanitized[key] = this.truncateAttribute(attributes[key], limit);
        }
      }
      return sanitized;
    },

    updateCart(attributes, note) {
      // SEC-C1: Sanitize attributes before sending to API
      var safeAttributes = this.sanitizeAttributes(attributes);
      var safeNote = this.truncateAttribute(note, 5000);

      const body = { attributes: safeAttributes };
      // The shopper's draft goes to cart.note (Order Notes) - an empty one too: that is a
      // deleted note. No draft (null): the cart's note is left as it is.
      if (typeof safeNote === 'string') {
        body.note = safeNote;
      }
      return CartSync.send('cart/update.js', body);
    }
  };

  // ==========================================================================
  // Initialize on DOM Ready
  // ==========================================================================
  async function init() {
    // Wait for DDMAvailability before init (prevents race condition JS-H3)
    const available = await waitForDDMAvailability(3000);
    if (!available) {
      console.warn('[PreCheckoutV2] Initializing without DDMAvailability - date validation may be limited');
    }
    PreCheckoutV2.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also handle page:loaded event (Shopify/Impulse pattern)
  document.addEventListener('page:loaded', async () => {
    console.log('[PreCheckoutV2] page:loaded event fired');
    await waitForDDMAvailability(1000); // Shorter timeout for re-init
    PreCheckoutV2.init();
  });

  // Reachable for tests and debugging, like window.DodoPrime.SmartCart
  // (tests/pre-checkout-cart-sync.test.js drives these modules in a Node VM)
  window.DodoPrime = window.DodoPrime || {};
  window.DodoPrime.PreCheckout = {
    PreCheckoutV2, StateManager, ModalController, CalendarModule, DeliveryOptionsModule, SidebarUpdater,
    NoticeManager, CartSync, NeedsInputModule, ExtrasDateCheck, CheckoutHandler
  };

})();
